// src/AdminVisitsCalendar.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import API from './api';
import { FaBookOpen, FaTrash, FaLock, FaBan } from 'react-icons/fa';
import { Building2 } from 'lucide-react';

/** ---------- Helpers ---------- */
function pad2(n) {
  return String(n).padStart(2, '0');
}
function addMinutesHHMM(hhmm, mins) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0, 0, 0);
  d.setMinutes(d.getMinutes() + (mins || 0));
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function normalizeTime(val) {
  if (!val) return '';
  let t = String(val);
  if (t.includes('AM') || t.includes('PM')) {
    const [hm, part] = t.split(' ');
    let [h, m] = hm.split(':').map(Number);
    if (part === 'PM' && h < 12) h += 12;
    if (part === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
  return t.slice(0, 5);
}
function toMinutes(t) {
  if (!t) return 0;
  const [h, m] = normalizeTime(t).split(':').map(Number);
  return h * 60 + m;
}
function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function groupByDate(visits) {
  return visits.reduce((acc, v) => {
    const date = String(v.scheduled_at || '').slice(0, 10);
    if (!date) return acc;
    if (!acc[date]) acc[date] = [];
    acc[date].push(v);
    return acc;
  }, {});
}
const isEventVisit = (v) =>
  v.type === 'event' ||
  v.visit_type === 'event' ||
  v.is_event === 1 ||
  v.is_event === true;

function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}
function overlaps(aFrom, aTo, bFrom, bTo) {
  const af = toMinutes(aFrom), at = toMinutes(aTo);
  const bf = toMinutes(bFrom), bt = toMinutes(bTo);
  return !(at <= bf || af >= bt);
}
function dayNameFromDateStr(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long' });
  } catch {
    return '';
  }
}

/** ---------- Org helper (super_admin delete requires organization_id query) ---------- */
function getUserFromStorage() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
}
function getActiveOrganizationIdForSuperAdmin() {
  const u = getUserFromStorage();
  if (u?.role !== 'super_admin') return null;

  const fromUser =
    u.organization_id ??
    u.active_organization_id ??
    u.active_org_id ??
    u.org_id ??
    null;

  const fromLs =
    localStorage.getItem('active_organization_id') ||
    localStorage.getItem('organization_id') ||
    localStorage.getItem('org_id') ||
    null;

  const v = fromUser ?? fromLs;
  const parsed = parseInt(String(v || ''), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/** ---------- Tiny Toast ---------- */
function Toast({ text, onClose }) {
  if (!text) return null;
  return (
    <div className="fixed top-4 right-4 z-[9999] text-white px-3 py-2 rounded shadow bg-slate-700">
      <div className="flex items-center gap-3">
        <span className="text-sm">{text}</span>
        <button onClick={onClose} className="text-white/90 hover:text-white text-lg leading-none">×</button>
      </div>
    </div>
  );
}

/** ---------- Badges ---------- */
function StatusBadge({ status, children }) {
  let color = 'bg-gray-200 text-gray-700';
  if (status === 'pending') color = 'bg-yellow-100 text-yellow-800';
  if (status === 'accepted') color = 'bg-green-100 text-green-700';
  if (status === 'rejected') color = 'bg-red-100 text-red-700';
  if (status === 'draft') color = 'bg-blue-100 text-blue-700';
  if (status === 'complete') color = 'bg-green-200 text-green-900';
  if (status === 'canceled') color = 'bg-purple-200 text-purple-700';
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${color}`}>
      {children || status}
    </span>
  );
}

function ProgramBadge({ program_type, discipline_code }) {
  const raw = (discipline_code || program_type || '').toString().trim();
  if (!raw) return null;
  const code = raw.replace(/^PROG_/i, '').toUpperCase();

  let extra = '';
  let label = code;

  if (code === 'PI') extra = ' bg-orange-100 text-orange-800 border border-orange-200';
  else if (code === 'EI') extra = ' bg-blue-100 text-blue-800 border border-blue-200';
  else if (code === 'ABA') extra = ' bg-purple-100 text-purple-800 border border-purple-200';
  else if (code === 'OT') extra = ' bg-teal-100 text-teal-800 border border-teal-200';
  else if (code === 'PT') extra = ' bg-emerald-100 text-emerald-800 border border-emerald-200';
  else if (code === 'ST') extra = ' bg-green-100 text-green-800 border border-green-200';
  else extra = ' border border-gray-400 text-gray-700 bg-gray-50';

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${extra}`}>
      {label}
    </span>
  );
}

function EventBadge({ isEvent }) {
  if (!isEvent) return null;
  return (
    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300">
      EVENT
    </span>
  );
}

function TelehealthBadge({ v }) {
  const isTele = (v?.delivery_mode || '').toLowerCase() === 'telehealth' || !!v?.telehealth_link;
  if (!isTele) return null;
  return (
    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-sky-100 text-sky-800 border border-sky-200">
      TELEHEALTH
    </span>
  );
}

function MiniDisciplineDots({ dayVisits }) {
  if (!dayVisits || dayVisits.length === 0) return null;

  const codes = new Set();
  let hasEvent = false;

  dayVisits.forEach((v) => {
    if (isEventVisit(v)) hasEvent = true;
    const raw = (v.discipline_code || v.program_type || '').toString().trim();
    if (!raw) return;
    const c = raw.replace(/^PROG_/i, '').toUpperCase();
    codes.add(c);
  });

  const dots = [];
  [...codes].slice(0, 4).forEach((code) => {
    let cls = 'bg-gray-400';
    if (code === 'EI') cls = 'bg-blue-400';
    else if (code === 'PI') cls = 'bg-orange-400';
    else if (code === 'ABA') cls = 'bg-purple-400';
    else if (code === 'OT') cls = 'bg-teal-400';
    else if (code === 'PT') cls = 'bg-emerald-400';
    else if (code === 'ST') cls = 'bg-green-400';

    dots.push(<span key={code} className={`w-2 h-2 rounded-full ${cls}`} title={code} />);
  });

  if (hasEvent) {
    dots.push(
      <span
        key="EVENT"
        className="w-2 h-2 rounded-full bg-yellow-200 border border-yellow-500"
        title="EVENT"
      />
    );
  }

  return <div className="flex flex-wrap gap-1 mt-1">{dots}</div>;
}

function VisitTime({ scheduled_at, event_end_time, event_end_time_calc }) {
  const from = String(scheduled_at || '').slice(11, 16) || '';
  let to = null;

  if (event_end_time_calc) to = String(event_end_time_calc).slice(0, 5);
  else if (event_end_time) to = String(event_end_time).slice(0, 5);
  else if (from) to = addMinutesHHMM(from, 45);

  return <span className="font-mono text-sm">{from && to ? `${from}–${to}` : from || '-'}</span>;
}

function StatPill({ label, value, tone = 'slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    green: 'bg-green-100 text-green-800 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    blue: 'bg-blue-100 text-blue-800 border-blue-200',
    purple: 'bg-purple-100 text-purple-800 border-purple-200',
    red: 'bg-red-100 text-red-800 border-red-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-semibold ${map[tone]}`}>
      <span className="opacity-80">{label}:</span>
      <span>{value}</span>
    </span>
  );
}

/** =========================================================
 * Distance pills
 * ========================================================= */
function fmtMi(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `${n.toFixed(2)} mi`;
}
function fmtMin(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `${Math.round(n)} min`;
}
function PrevRefPill({ prev_ref }) {
  const end = prev_ref?.end_time || prev_ref?.end || prev_ref?.to || null;
  if (!end) return null;
  return (
    <span className="text-[11px] text-gray-500 ml-2">
      from last stop {String(end)}
    </span>
  );
}
function DistanceLine({ item, distanceMode = 'haversine' }) {
  if (!item) return null;
  const mode = String(distanceMode || '').toLowerCase();
  if (mode === 'none') return null;

  const rmi = item?.route_mi;
  const rmn = item?.route_min;
  const sli = item?.straight_line_mi;
  const src = (item?.location_source || '').toString().toLowerCase();

  const parts = [];
  const rMiTxt = fmtMi(rmi);
  const rMinTxt = fmtMin(rmn);
  const slTxt = fmtMi(sli);

  const showRoute = mode === 'ors';
  const showStraight = mode === 'haversine' || mode === 'ors';

  if (showRoute) {
    if (rMiTxt && rMinTxt) parts.push(`${rMiTxt} (${rMinTxt})`);
    else if (rMiTxt) parts.push(rMiTxt);
  }
  if (showStraight && slTxt) parts.push(`straight ${slTxt}`);

  if (src === 'alt') parts.push('ALT address');
  else if (src === 'primary') parts.push('Primary address');

  if (!parts.length) return null;
  return <div className="text-xs text-gray-600">{parts.join(' • ')}</div>;
}

/** ---------- Discipline rules ---------- */
const DISCIPLINE_BUTTONS = [
  { code: 'PROG_EI', short: 'EI', label: 'Early Intervention', chipClass: 'bg-blue-100 text-blue-700' },
  { code: 'PROG_PI', short: 'PI', label: 'Project Impact', chipClass: 'bg-orange-100 text-orange-700' },
  { code: 'PROG_ABA', short: 'ABA', label: 'ABA', chipClass: 'bg-purple-100 text-purple-700' },
  { code: 'PROG_OT', short: 'OT', label: 'Occupational Therapy', chipClass: 'bg-teal-100 text-teal-700' },
  { code: 'PROG_PT', short: 'PT', label: 'Physical Therapy', chipClass: 'bg-lime-100 text-lime-700' },
  { code: 'PROG_ST', short: 'ST', label: 'Speech Therapy', chipClass: 'bg-green-100 text-green-700' },
];

function getDurationConfig(disciplineCode, type, deliveryMode = 'in_person') {
  if ((deliveryMode || '').toLowerCase() === 'telehealth') return { base: 60, min: 45, max: 90 };
  if (type === 'event') return { base: 90, min: 45, max: 240 }; // legacy only
  switch (disciplineCode) {
    case 'PROG_EI': return { base: 45, min: 45, max: 45 };
    case 'PROG_PI': return { base: 90, min: 90, max: 90 };
    case 'PROG_ABA': return { base: 90, min: 45, max: 240 };
    case 'PROG_OT':
    case 'PROG_PT': return { base: 45, min: 45, max: 90 };
    case 'PROG_ST': return { base: 45, min: 45, max: 45 };
    default: return { base: 45, min: 45, max: 90 };
  }
}
function getMinMaxDuration(disciplineCode, type, deliveryMode) {
  const cfg = getDurationConfig(disciplineCode, type, deliveryMode);
  return { min: cfg.min, max: cfg.max };
}
function isHardLimited(disciplineCode, type, deliveryMode) {
  const { min, max } = getMinMaxDuration(disciplineCode, type, deliveryMode);
  return min === max;
}

function DisciplineButtonsRow({ student, isEligible = true, onSelectDiscipline, selectedCode }) {
  const programs = Array.isArray(student.program_codes)
    ? student.program_codes
    : Array.isArray(student.disciplines)
      ? student.disciplines
      : [];

  const taken = new Set(Array.isArray(student.taken_program_codes) ? student.taken_program_codes : []);
  const hasPrograms = programs.length > 0;
  if (!hasPrograms) return null;

  return (
    <div className="flex flex-wrap gap-1 justify-end">
      {DISCIPLINE_BUTTONS.map(def => {
        const hasDisc = programs.includes(def.code);
        const already = taken.has(def.code);
        const disabled = !isEligible || !hasDisc || already;
        const isSelected = !disabled && selectedCode === def.code;

        return (
          <button
            key={def.code}
            type="button"
            disabled={disabled}
            title={
              !isEligible ? 'Not eligible for this time-slot.' :
                !hasDisc ? 'Discipline not assigned.' :
                  already ? 'Already has this discipline today.' :
                    'Select discipline.'
            }
            onClick={() => !disabled && onSelectDiscipline(def.code)}
            className={
              'px-2 py-0.5 rounded text-xs border transition ' +
              (disabled
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : isSelected
                  ? `${def.chipClass} border-blue-500 ring-1 ring-blue-400`
                  : `${def.chipClass} border-transparent hover:opacity-90`)
            }
          >
            {def.short}
          </button>
        );
      })}
    </div>
  );
}

/** ---------- Color palette ---------- */
const COLOR_PALETTE = [
  'bg-emerald-400', 'bg-sky-400', 'bg-fuchsia-400',
  'bg-amber-400', 'bg-lime-400', 'bg-rose-400',
  'bg-cyan-400', 'bg-violet-400', 'bg-orange-400'
];
function colorForId(id) {
  if (!id && id !== 0) return 'bg-gray-400';
  const idx = Math.abs(parseInt(id, 10)) % COLOR_PALETTE.length;
  return COLOR_PALETTE[idx];
}

/** ---------- Timeline drawing helpers ---------- */
function pctBox(from, to, dayStartM, spanM) {
  const f = toMinutes(from);
  const t = toMinutes(to);
  const left = ((f - dayStartM) / spanM) * 100;
  const width = ((t - f) / spanM) * 100;
  return {
    left: `${Math.max(0, left)}%`,
    width: `${Math.max(0, Math.min(100 - left, width))}%`,
  };
}
function pctBoxM(fromM, toM, dayStartM, spanM) {
  const left = ((fromM - dayStartM) / spanM) * 100;
  const width = ((toM - fromM) / spanM) * 100;
  const safeLeft = Math.max(0, left);
  const safeWidth = Math.max(0, Math.min(100 - safeLeft, width));
  return { left: `${safeLeft}%`, width: `${safeWidth}%` };
}

function getBusyFromTo(v) {
  const from =
    normalizeTime(v?.from) ||
    normalizeTime(v?.from_time) ||
    (v?.scheduled_at ? String(v.scheduled_at).slice(11, 16) : '');
  let to =
    normalizeTime(v?.to) ||
    normalizeTime(v?.to_time) ||
    normalizeTime(v?.to_hhmm) ||
    normalizeTime(v?.event_end_time_calc) ||
    normalizeTime(v?.event_end_time);

  if (!to && from) {
    const mins = String(v?.type) === 'event' ? 90 : 45;
    to = addMinutesHHMM(from, mins);
  }
  return { from, to };
}

/** ---------- Work helpers ---------- */
function isWorkBlock(v) {
  if (!v) return false;
  const kind = String(v.kind || '').toLowerCase();
  const type = String(v.type || '').toLowerCase();
  const busyAs = String(v.busy_as || '').toLowerCase();
  return kind === 'work' || type === 'work' || busyAs === 'work';
}
function isWorkCandidate(c) {
  if (!c) return false;
  const ck = String(c.candidate_kind || '').toLowerCase();
  if (ck === 'work') return true;
  if (!c.student && c.work_type) return true;
  return false;
}
function getWorkId(v) {
  return v?.work_id || v?.id || null;
}
function getWorkDate(v, selectedDate) {
  return (
    (v?.scheduled_at ? String(v.scheduled_at).slice(0, 10) : null) ||
    (v?.date ? String(v.date).slice(0, 10) : null) ||
    selectedDate ||
    null
  );
}
const WORK_CANCEL_QUICK_REASONS = [
  'Schedule changed — work task moved to another time.',
  'Task no longer needed — completed elsewhere.',
  'Provider unavailable — will reschedule.',
];

/** ---------- Student availability helpers ---------- */
function makeAvailKey(date, studentId) {
  return `${String(date || '').slice(0, 10)}|${String(studentId)}`;
}
function normalizeAvailabilityIntervals(payload) {
  const raw =
    payload?.intervals ||
    payload?.availability ||
    payload?.slots ||
    payload?.data?.intervals ||
    payload?.data?.availability ||
    payload?.data ||
    [];

  const arr = Array.isArray(raw)
    ? raw
    : (raw && typeof raw === 'object')
      ? (Array.isArray(raw.intervals) ? raw.intervals : (Array.isArray(raw.availability) ? raw.availability : []))
      : [];

  const out = [];
  for (const it of arr) {
    const f = normalizeTime(it?.from || it?.start || it?.from_time || it?.time_from || it?.begin || it?.s);
    const t = normalizeTime(it?.to || it?.end || it?.to_time || it?.time_to || it?.finish || it?.e);
    const fM = toMinutes(f);
    const tM = toMinutes(t);
    if (!f || !t) continue;
    if (!Number.isFinite(fM) || !Number.isFinite(tM)) continue;
    if (tM <= fM) continue;
    out.push({ fromM: fM, toM: tM });
  }

  out.sort((a, b) => a.fromM - b.fromM);

  const merged = [];
  for (const seg of out) {
    const last = merged[merged.length - 1];
    if (!last) merged.push({ ...seg });
    else if (seg.fromM <= last.toM) last.toM = Math.max(last.toM, seg.toM);
    else merged.push({ ...seg });
  }
  return merged;
}
function getAvailabilityBoundsForCandidate(intervals, candFromM, candToM) {
  const list = Array.isArray(intervals) ? intervals : [];
  if (!list.length) return null;

  const containing = list.find(x => candFromM >= x.fromM && candToM <= x.toM);
  if (containing) return { fromM: containing.fromM, toM: containing.toM };

  let best = null;
  let bestOverlap = -1;
  for (const x of list) {
    const ov = Math.min(candToM, x.toM) - Math.max(candFromM, x.fromM);
    if (ov > bestOverlap) {
      bestOverlap = ov;
      best = x;
    }
  }
  if (best && bestOverlap > 0) return { fromM: best.fromM, toM: best.toM };

  let nearest = list[0];
  let bestDist = Math.abs(candFromM - list[0].fromM);
  for (const x of list) {
    const d = Math.abs(candFromM - x.fromM);
    if (d < bestDist) {
      bestDist = d;
      nearest = x;
    }
  }
  return nearest ? { fromM: nearest.fromM, toM: nearest.toM } : null;
}

/** ---------- Main component ---------- */
export default function AdminVisitsCalendar() {
  const [monthCursor, setMonthCursor] = useState(null);
  const [todayStr, setTodayStr] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // Provider selection for timelines + filtering
  const [providerMode, setProviderMode] = useState('none'); // 'none' | 'all' | 'selected'
  const [selectedProviderIds, setSelectedProviderIds] = useState([]); // array of teacher ids (strings)
  const [showProviderPicker, setShowProviderPicker] = useState(false);
  const [providerSearch, setProviderSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [toastText, setToastText] = useState('');

  // ✅ Cancel modal (visit: dropdown reason, work: short text)
  const [cancelMode, setCancelMode] = useState('visit'); // 'visit' | 'work'
  const [reason, setReason] = useState('');
  const REASONS = [
    'Cancelled by teacher',
    'Cancelled by family',
    'Late cancellation',
    'NoShow/withAppointmentConfirmation',
  ];
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  // Notes history modal
  const [showNotesHistory, setShowNotesHistory] = useState(false);
  const [notesHistory, setNotesHistory] = useState([]);
  const [notesHistoryLoading, setNotesHistoryLoading] = useState(false);
  const [notesHistoryStudent, setNotesHistoryStudent] = useState(null);

  /** ✅ Service duration rules (system_settings: service_duration_rules) */
  const [serviceDurationRules, setServiceDurationRules] = useState(null);
  const [serviceDurationLoading, setServiceDurationLoading] = useState(false);

  /** Day Timelines state */
  const [workday, setWorkday] = useState({ start: '08:00', end: '18:00' });
  const [allTeachers, setAllTeachers] = useState([]);
  const [allStudents, setAllStudents] = useState([]);

  const [dayPlannerLoading, setDayPlannerLoading] = useState(false);
  const [dayPlanner, setDayPlanner] = useState({ byTeacher: {} });

  // candidates only for selectedDate, grouped by teacher
  const [candidatesByTeacher, setCandidatesByTeacher] = useState({});

  /** ✅ Student availability cache (date|studentId => intervals minutes) */
  const [studentAvailability, setStudentAvailability] = useState({});
  const availInflightRef = useRef(new Map());

  /** Modal (select student) */
  const [showModal, setShowModal] = useState(false);
  const [modalTeacherId, setModalTeacherId] = useState(null);
  const [modalDate, setModalDate] = useState('');
  const [modalFrom, setModalFrom] = useState('');
  const [modalMaxTo, setModalMaxTo] = useState('');
  const [modalAnchor, setModalAnchor] = useState({ from: '', to: '' });

  /** ✅ NEW: visit location mode (at_home default) */
  const [visitLocationMode, setVisitLocationMode] = useState('at_home'); // at_home | at_clinic
  const [selectedClinic, setSelectedClinic] = useState(null); // {id,name,geocoded_label}
  const [clinicModalOpen, setClinicModalOpen] = useState(false);

  const [eligibleDefault, setEligibleDefault] = useState([]);
  const [otherEligible, setOtherEligible] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);

  const [distanceMode, setDistanceMode] = useState('haversine'); // none|haversine|ors

  // Telehealth modal
  const [showTeleModal, setShowTeleModal] = useState(false);
  const [teleStudent, setTeleStudent] = useState(null);
  const [teleLink, setTeleLink] = useState('');
  const [teleError, setTeleError] = useState('');
  const [teleDisciplineCode, setTeleDisciplineCode] = useState(null);

  // preserve clinic selection for telehealth
  const [teleVisitLocationMode, setTeleVisitLocationMode] = useState('at_home');
  const [teleOrgLocationId, setTeleOrgLocationId] = useState(null);

  /** Work modal */
  const [workModalOpen, setWorkModalOpen] = useState(false);
  const [orgLocations, setOrgLocations] = useState([]);
  const [orgLocationsLoading, setOrgLocationsLoading] = useState(false);
  const [workSubmitting, setWorkSubmitting] = useState(false);

  const [workForm, setWorkForm] = useState({
    location_value: '', // "org:<id>" | "teacher_home" | "remote"
    work_type: 'admin',
    planned_minutes: '60',
    title: '',
    notes: '',
  });

  // selected discipline per student in modal
  const [selectedDisciplines, setSelectedDisciplines] = useState({});

  const fetchRef = useRef(0);
  const lastEligibleKeyRef = useRef('');
  const trackRefs = useRef({});

  /** Drag/Resize state (candidates) */
  const [drag, setDrag] = useState(null);

  /** Match right panel height to calendar (lg+) */
  const calendarBoxRef = useRef(null);
  const [calendarHeight, setCalendarHeight] = useState(null);
  const [isLg, setIsLg] = useState(false);

  const selectedDayName = useMemo(() => dayNameFromDateStr(selectedDate), [selectedDate]);
  const isPastSelectedDate = useMemo(() => {
    if (!selectedDate || !todayStr) return false;
    return selectedDate < todayStr;
  }, [selectedDate, todayStr]);

  useEffect(() => {
    const update = () => setIsLg(typeof window !== 'undefined' && window.innerWidth >= 1024);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!calendarBoxRef.current) return;

    const el = calendarBoxRef.current;
    const update = () => {
      const h = el.getBoundingClientRect().height;
      setCalendarHeight(h > 0 ? Math.round(h) : null);
    };

    update();

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, [monthCursor, loading, selectedDate, providerMode, selectedProviderIds.join('|'), statusFilter, visits.length]);

  // Auto-clear toast
  useEffect(() => {
    if (!toastText) return;
    const t = setTimeout(() => setToastText(''), 3000);
    return () => clearTimeout(t);
  }, [toastText]);

  /** Load server time => today, monthCursor */
  useEffect(() => {
    let isMounted = true;
    API.get('/server-time')
      .then((res) => {
        if (!isMounted) return;
        const dateStr = String(res.data?.serverTime || '').slice(0, 10);
        if (dateStr) {
          setTodayStr(dateStr);
          const [y, m] = dateStr.split('-').map(Number);
          setMonthCursor(new Date(y, m - 1, 1));
        } else {
          throw new Error('Missing serverTime');
        }
      })
      .catch(() => {
        const d = new Date();
        const dateStr = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
        setTodayStr(dateStr);
        setMonthCursor(new Date(d.getFullYear(), d.getMonth(), 1));
      });

    return () => { isMounted = false; };
  }, []);

  /** Load workday settings */
  useEffect(() => {
    const u = getUserFromStorage();
    const isSuper = u?.role === 'super_admin';
    const orgId = isSuper ? getActiveOrganizationIdForSuperAdmin() : null;

    const cfg = (isSuper && orgId)
      ? { params: { organization_id: orgId } }
      : undefined;

    API.get('/workday-settings', cfg)
      .then(res => {
        const start = String(res.data?.start || '08:00').slice(0, 5);
        const end = String(res.data?.end || '18:00').slice(0, 5);
        if (toMinutes(end) <= toMinutes(start)) {
          setWorkday({ start: '08:00', end: '18:00' });
        } else {
          setWorkday({ start, end });
        }
      })
      .catch(() => setWorkday({ start: '08:00', end: '18:00' }));
  }, []);

  /** ✅ Load service duration settings */
  useEffect(() => {
    let alive = true;
    setServiceDurationLoading(true);

    API.get('/service-duration-settings')
      .then((res) => {
        if (!alive) return;
        setServiceDurationRules(res.data || null);
      })
      .catch(() => {
        if (!alive) return;
        setServiceDurationRules(null);
      })
      .finally(() => {
        if (!alive) return;
        setServiceDurationLoading(false);
      });

    return () => { alive = false; };
  }, []);

  /** Load teachers + students for scheduling modal */
  useEffect(() => {
    API.get('/teachers/planner')
      .then(res => setAllTeachers(res.data || []))
      .catch(() => setAllTeachers([]));

    API.get('/admin/students?assigned_only=0')
      .then(res => setAllStudents(res.data || []))
      .catch(() => setAllStudents([]));
  }, []);

  /** Load visits for month */
  useEffect(() => {
    if (!monthCursor) return;

    const y = monthCursor.getFullYear();
    const m = monthCursor.getMonth() + 1;

    const from = `${y}-${pad2(m)}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const to = `${y}-${pad2(m)}-${pad2(lastDay)}`;

    async function load() {
      setLoading(true);
      try {
        const res = await API.get('/admin/visits-calendar', { params: { date_from: from, date_to: to } });
        const rows = res.data || [];
        setVisits(rows);

        setSelectedDate((prev) => {
          if (prev) return prev;
          if (todayStr && todayStr.slice(0, 7) === from.slice(0, 7)) return todayStr;
          const groupedLocal = groupByDate(rows);
          const dates = Object.keys(groupedLocal).sort();
          return dates.length ? dates[0] : null;
        });
      } catch (err) {
        console.error('Failed to load admin visits for month:', err);
        setVisits([]);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [monthCursor, todayStr]);

  /** ✅ Duration rules helpers (settings-first, fallback to defaults) */
  function stripProg(code) {
    return String(code || '').replace(/^PROG_/i, '').toUpperCase().trim();
  }
  function ruleToCfg(rule) {
    if (!rule || typeof rule !== 'object') return null;
    const type = String(rule.type || '').toLowerCase();

    if (type === 'fixed') {
      const fixed = Number(rule.fixed);
      if (!Number.isFinite(fixed) || fixed <= 0) return null;
      return { base: fixed, min: fixed, max: fixed };
    }

    if (type === 'range') {
      const def = Number(rule.default);
      const min = Number(rule.min);
      const max = Number(rule.max);
      if (!Number.isFinite(min) || !Number.isFinite(max) || max <= 0 || max < min) return null;

      const base = Number.isFinite(def) && def >= min && def <= max ? def : min;
      return { base, min, max };
    }

    return null;
  }
  function getDurationConfigEffective(disciplineCode, type, deliveryMode = 'in_person') {
    const rules = serviceDurationRules;

    if ((deliveryMode || '').toLowerCase() === 'telehealth') {
      const cfgFromSettings = ruleToCfg(rules?.TELEHEALTH);
      return cfgFromSettings || getDurationConfig(disciplineCode, type, deliveryMode);
    }
    if (type === 'event') {
      const cfgFromSettings = ruleToCfg(rules?.EVENT);
      return cfgFromSettings || getDurationConfig(disciplineCode, type, deliveryMode);
    }
    const key = stripProg(disciplineCode);
    const cfgFromSettings = ruleToCfg(rules?.[key]);
    return cfgFromSettings || getDurationConfig(disciplineCode, type, deliveryMode);
  }
  function getDisciplineDurationEffective(disciplineCode, type, deliveryMode) {
    return getDurationConfigEffective(disciplineCode, type, deliveryMode).base;
  }
  function getMinMaxDurationEffective(disciplineCode, type, deliveryMode) {
    const cfg = getDurationConfigEffective(disciplineCode, type, deliveryMode);
    return { min: cfg.min, max: cfg.max };
  }
  function isHardLimitedEffective(disciplineCode, type, deliveryMode) {
    const { min, max } = getMinMaxDurationEffective(disciplineCode, type, deliveryMode);
    return min === max;
  }

  const durationSummaryText = useMemo(() => {
    const items = [
      { key: 'EI', label: 'EI' },
      { key: 'PI', label: 'PI' },
      { key: 'ST', label: 'ST' },
      { key: 'ABA', label: 'ABA' },
      { key: 'OT', label: 'OT' },
      { key: 'PT', label: 'PT' },
      { key: 'EVENT', label: 'EVENT' }, // legacy display only
    ];

    const parts = items.map(({ key, label }) => {
      const cfg = ruleToCfg(serviceDurationRules?.[key]);
      if (cfg) {
        if (cfg.min === cfg.max) return `${label} ${cfg.base}m`;
        return `${label} ${cfg.min}–${cfg.max}m (def ${cfg.base})`;
      }

      const fallbackDisc = key === 'EVENT' ? null : `PROG_${key}`;
      const fb = getDurationConfig(fallbackDisc, key === 'EVENT' ? 'event' : 'standard', 'in_person');
      if (fb.min === fb.max) return `${label} ${fb.base}m`;
      return `${label} ${fb.min}–${fb.max}m (def ${fb.base})`;
    });

    return parts.join(' • ');
  }, [serviceDurationRules]);

  /** Provider options */
  const teacherOptions = useMemo(() => {
    return (allTeachers || [])
      .map(t => ({ id: String(t.id), name: t.name || `Teacher ${t.id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allTeachers]);

  const selectedProviderSet = useMemo(() => {
    if (providerMode !== 'selected') return null;
    if (!selectedProviderIds || selectedProviderIds.length === 0) return null;
    return new Set(selectedProviderIds.map(String));
  }, [providerMode, selectedProviderIds]);

  const selectedProviderNames = useMemo(() => {
    const map = new Map((teacherOptions || []).map(t => [String(t.id), t.name]));
    return (selectedProviderIds || []).map(id => map.get(String(id)) || `Teacher ${id}`);
  }, [teacherOptions, selectedProviderIds]);

  const providerButtonLabel = useMemo(() => {
    if (providerMode === 'all') return 'All providers';
    if (providerMode === 'selected') {
      const n = (selectedProviderIds || []).length;
      if (!n) return 'Select providers…';
      if (n <= 2) return selectedProviderNames.join(', ');
      return `${n} selected`;
    }
    return 'Select providers…';
  }, [providerMode, selectedProviderIds, selectedProviderNames]);

  const toggleProviderId = (idStr) => {
    const id = String(idStr);
    setProviderMode('selected');
    setSelectedProviderIds(prev => {
      const cur = Array.isArray(prev) ? prev.slice() : [];
      const has = cur.includes(id);
      const next = has ? cur.filter(x => x !== id) : [...cur, id];
      return next;
    });
  };

  const selectAllProviders = () => {
    setProviderMode('all');
    setSelectedProviderIds([]);
  };

  const clearProviderSelection = () => {
    setProviderMode('selected');
    setSelectedProviderIds([]);
  };

  const providerPickerList = useMemo(() => {
    const q = (providerSearch || '').trim().toLowerCase();
    if (!q) return teacherOptions;
    return (teacherOptions || []).filter(t => (t.name || '').toLowerCase().includes(q));
  }, [teacherOptions, providerSearch]);

  /** Filtered visits (calendar + right panel) */
  const filteredVisits = useMemo(() => {
    return (visits || []).filter((v) => {
      if (selectedProviderSet && !selectedProviderSet.has(String(v.teacher_id))) return false;
      if (statusFilter !== 'all' && String(v.status) !== String(statusFilter)) return false;
      return true;
    });
  }, [visits, selectedProviderSet, statusFilter]);

  const grouped = useMemo(() => groupByDate(filteredVisits), [filteredVisits]);

  const monthStats = useMemo(() => {
    const all = filteredVisits;
    const total = all.length;
    const draft = all.filter((v) => v.status === 'draft').length;
    const pending = all.filter((v) => v.status === 'pending').length;
    const complete = all.filter((v) => v.status === 'complete').length;
    const canceled = all.filter((v) => v.status === 'canceled').length;
    const subs = all.filter((v) => v.original_teacher_id && String(v.teacher_id) !== String(v.original_teacher_id)).length;
    return { total, draft, pending, complete, canceled, subs };
  }, [filteredVisits]);

  /** Month nav */
  const handlePrevMonth = () => {
    if (!monthCursor) return;
    const d = new Date(monthCursor);
    d.setMonth(d.getMonth() - 1);
    setMonthCursor(d);
  };
  const handleNextMonth = () => {
    if (!monthCursor) return;
    const d = new Date(monthCursor);
    d.setMonth(d.getMonth() + 1);
    setMonthCursor(d);
  };
  const goToToday = () => {
    if (!todayStr) return;
    const [y, m] = todayStr.split('-').map(Number);
    setMonthCursor(new Date(y, m - 1, 1));
    setSelectedDate(todayStr);
  };

  function buildCalendarCells() {
    if (!monthCursor) return [];
    const y = monthCursor.getFullYear();
    const m = monthCursor.getMonth();
    const firstOfMonth = new Date(y, m, 1);
    const firstWeekday = firstOfMonth.getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(y, m, day);
      const dateStr = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
      cells.push({ dateObj: d, dateStr });
    }
    return cells;
  }

  const calendarCells = buildCalendarCells();
  const monthLabel = monthCursor
    ? monthCursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '';

  /** Notes history modal */
  const openNotesHistoryModal = async (studentId, studentName) => {
    setNotesHistoryLoading(true);
    setShowNotesHistory(true);
    setNotesHistoryStudent(studentName);
    try {
      const res = await API.get(`/students/${studentId}/notes-history`);
      setNotesHistory(res.data || []);
    } catch {
      setNotesHistory([]);
    } finally {
      setNotesHistoryLoading(false);
    }
  };

  const selectedVisits = selectedDate ? grouped[selectedDate] || [] : [];

  /** ---------- Timeline provider selection logic ---------- */
  const effectiveTimelineTeacherIds = useMemo(() => {
    if (!selectedDate) return [];

    const allIds = (allTeachers || []).map(t => String(t.id));
    const allSet = new Set(allIds);

    if (providerMode === 'all') return allIds;

    if (providerMode === 'selected') {
      const raw = Array.isArray(selectedProviderIds) ? selectedProviderIds.map(String) : [];
      const cleaned = raw.filter(id => allSet.has(id));
      const deduped = [];
      const seen = new Set();
      for (const id of cleaned) {
        if (seen.has(id)) continue;
        seen.add(id);
        deduped.push(id);
      }
      return deduped;
    }

    return [];
  }, [providerMode, selectedProviderIds, allTeachers, selectedDate]);

  // Reset candidates when switching day or provider selection
  useEffect(() => {
    setCandidatesByTeacher({});
  }, [selectedDate, providerMode, selectedProviderIds.join('|')]);

  /** ---------- Load day planner data from BE ---------- */
  async function loadDayPlanner() {
    if (!selectedDate) return;

    const teacherIds = effectiveTimelineTeacherIds;
    if (!teacherIds || teacherIds.length === 0) return;

    setDayPlannerLoading(true);
    try {
      const res = await API.get('/admin/day-planner', {
        params: {
          date: selectedDate,
          teacher_ids: teacherIds.join(','),
        },
      });
      setDayPlanner(res.data || { byTeacher: {} });
    } catch (e) {
      console.error('Failed to load day planner:', e);
      setDayPlanner({ byTeacher: {} });
    } finally {
      setDayPlannerLoading(false);
    }
  }

  useEffect(() => {
    loadDayPlanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, providerMode, selectedProviderIds.join('|'), effectiveTimelineTeacherIds.join('|')]);

  /** ---------- Candidate constraints (NO EVENT in new flow) ---------- */
  const takenIndex = useMemo(() => {
    const takenDisc = new Set(); // `${studentId}|${disc}`

    const byTeacher = dayPlanner?.byTeacher || {};
    for (const tid of Object.keys(byTeacher)) {
      const busy = Array.isArray(byTeacher[tid]?.busy) ? byTeacher[tid].busy : [];
      for (const v of busy) {
        if (String(v.status) === 'canceled') continue;
        const sid = v.student_id;
        if (!sid) continue;
        const disc = v.discipline_code ? String(v.discipline_code) : 'GEN';
        takenDisc.add(`${sid}|${disc}`);
      }
    }

    for (const tid of Object.keys(candidatesByTeacher || {})) {
      const list = candidatesByTeacher[tid] || [];
      for (const c of list) {
        const sid = c?.student?.id;
        if (!sid) continue;
        const disc = c.discipline_code ? String(c.discipline_code) : 'GEN';
        takenDisc.add(`${sid}|${disc}`);
      }
    }

    return { takenDisc };
  }, [dayPlanner, candidatesByTeacher]);

  function canAddCandidate(studentId, disciplineCode) {
    const sid = String(studentId);
    const disc = disciplineCode ? String(disciplineCode) : 'GEN';
    if (takenIndex.takenDisc.has(`${sid}|${disc}`)) {
      return { ok: false, reason: 'This student already has this discipline today.' };
    }
    return { ok: true };
  }

  /** =========================================================
   * ✅ Student availability: cache + dedupe
   * ========================================================= */
  async function ensureStudentAvailability(studentId, dateStr) {
    const sid = Number(studentId);
    const d = String(dateStr || '').slice(0, 10);
    if (!sid || !d) return [];

    const key = makeAvailKey(d, sid);
    const existing = studentAvailability[key];
    if (existing) return existing;

    if (availInflightRef.current.has(key)) {
      return availInflightRef.current.get(key);
    }

    const p = API.get(`/students/${sid}/availability`, { params: { date: d } })
      .then((res) => {
        const intervals = normalizeAvailabilityIntervals(res.data);
        setStudentAvailability(prev => ({ ...prev, [key]: intervals }));
        return intervals;
      })
      .catch(() => {
        setStudentAvailability(prev => ({ ...prev, [key]: [] }));
        return [];
      })
      .finally(() => {
        availInflightRef.current.delete(key);
      });

    availInflightRef.current.set(key, p);
    return p;
  }

  /** -------- ORG LOCATIONS loader (shared for Work + Clinic picker) -------- */
  async function loadOrgLocations() {
    if (orgLocationsLoading) return;
    setOrgLocationsLoading(true);
    try {
      const res = await API.get('/org-locations');
      const locs = Array.isArray(res.data?.locations) ? res.data.locations : [];
      setOrgLocations(locs);

      const defId = res.data?.clinic_settings?.default_location_id;
      const hasDef = defId && locs.some(l => String(l.id) === String(defId));
      setWorkForm(prev => {
        if (prev.location_value) return prev;
        if (hasDef) return { ...prev, location_value: `org:${defId}` };
        if (locs.length) return { ...prev, location_value: `org:${locs[0].id}` };
        return prev;
      });
    } catch (e) {
      console.error('loadOrgLocations failed:', e);
      setOrgLocations([]);
    } finally {
      setOrgLocationsLoading(false);
    }
  }

  /** ✅ Clinic label + detection (list UI) */
  function resolveClinicLabel(orgLocationId) {
    if (!orgLocationId) return null;
    const found = (orgLocations || []).find(l => String(l.id) === String(orgLocationId));
    if (!found) return `Clinic #${orgLocationId}`;
    const name = found.name || `Clinic #${orgLocationId}`;
    const extra = found.geocoded_label ? ` — ${found.geocoded_label}` : '';
    return `${name}${extra}`;
  }
  function isClinicLikeItem(raw, isWork) {
    const lm = String(raw?.location_mode || '').toLowerCase();
    const oid = raw?.org_location_id ? Number(raw.org_location_id) : null;
    if (!oid) return false;

    // visit: at_clinic + org_location_id
    if (!isWork && lm === 'at_clinic') return true;

    // work: org_location + org_location_id
    if (isWork && lm === 'org_location') return true;

    return false;
  }

  // Auto-load org locations if we detect clinic-like items on screen (saved or candidates)
  useEffect(() => {
    const byTeacher = dayPlanner?.byTeacher || {};
    let needs = false;

    for (const tid of Object.keys(byTeacher)) {
      const busy = Array.isArray(byTeacher[tid]?.busy) ? byTeacher[tid].busy : [];
      for (const v of busy) {
        const isWork = isWorkBlock(v);
        if (isClinicLikeItem(v, isWork)) { needs = true; break; }
      }
      if (needs) break;
    }

    if (!needs) {
      for (const tid of Object.keys(candidatesByTeacher || {})) {
        const list = Array.isArray(candidatesByTeacher[tid]) ? candidatesByTeacher[tid] : [];
        for (const c of list) {
          const isWork = isWorkCandidate(c);
          if (isClinicLikeItem(c, isWork)) { needs = true; break; }
        }
        if (needs) break;
      }
    }

    if (needs && !orgLocationsLoading && (!orgLocations || orgLocations.length === 0)) {
      loadOrgLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayPlanner, candidatesByTeacher]);

  /** ✅ Clinic picker open */
  function openClinicPicker() {
    if (!orgLocations.length) loadOrgLocations();
    setClinicModalOpen(true);
  }

  /** =========================================================
   * ✅ Origin override from UNSAVED candidates (clinic-only)
   * ========================================================= */
  function buildOriginOverrideForModalInterval(tId, dateStr, fromHHMM) {
    try {
      const tid = String(tId || '');
      const d = String(dateStr || '').slice(0, 10);
      const fromM = toMinutes(fromHHMM);

      const list = Array.isArray(candidatesByTeacher?.[tid]) ? candidatesByTeacher[tid] : [];
      if (!list.length) return null;

      let best = null;
      let bestToM = -1;

      for (const c of list) {
        if (!c) continue;
        if (String(c.date || '').slice(0, 10) !== d) continue;

        // ignore work candidates for origin override
        if (isWorkCandidate(c)) continue;

        const toM = toMinutes(c.to);
        if (!Number.isFinite(toM)) continue;
        if (toM > fromM) continue;

        if (toM > bestToM) {
          bestToM = toM;
          best = c;
        }
      }

      if (!best) return null;

      if (String(best.location_mode || '').toLowerCase() === 'at_clinic' && best.org_location_id) {
        const hhmm = normalizeTime(best.to);
        return {
          origin_type: 'org_location',
          org_location_id: Number(best.org_location_id),
          end_time: hhmm ? `${hhmm}:00` : null
        };
      }

      return null;
    } catch {
      return null;
    }
  }

  /** =========================================================
   * ✅ eligible-for-interval fetch (destination override + origin_override)
   * ========================================================= */
  async function fetchEligibleForModal({
    teacherId,
    date,
    from,
    maxTo,
    openModalIfClosed = true,
    preserveExisting = true,
  }) {
    if (!teacherId || !date || !from || !maxTo) return;

    const SLOT_MIN = 45;
    const toDefault = minutesToHHMM(Math.min(toMinutes(from) + SLOT_MIN, toMinutes(maxTo)));

    const clinicKey = (visitLocationMode === 'at_clinic' && selectedClinic?.id)
      ? `clinic:${selectedClinic.id}`
      : 'home';

    const originKey = (() => {
      const ovr = buildOriginOverrideForModalInterval(teacherId, date, from);
      if (!ovr) return 'no-ovr';
      if (ovr.origin_type === 'org_location') return `ovr:org:${ovr.org_location_id}`;
      return 'ovr:other';
    })();

    const key = [
      String(teacherId),
      String(date),
      String(from),
      String(maxTo),
      String(distanceMode),
      String(clinicKey),
      String(originKey),
    ].join('|');

    lastEligibleKeyRef.current = key;
    const reqId = ++fetchRef.current;

    if (!preserveExisting) {
      setEligibleDefault([]);
      setOtherEligible([]);
      setSelectedDisciplines({});
    }

    setLoadingEligible(true);

    try {
      const payload = {
        teacher_id: Number(teacherId),
        date,
        from,
        to: toDefault,
        limit: 500,
        distance_mode: distanceMode,
        sort_by_distance: true,

        // ✅ destination override (At clinic)
        visit_location_mode: visitLocationMode,
        org_location_id: (visitLocationMode === 'at_clinic' && selectedClinic?.id)
          ? Number(selectedClinic.id)
          : null,

        // ✅ origin override (unsaved chain)
        origin_override: buildOriginOverrideForModalInterval(teacherId, date, from),
      };

      const r = await API.post('/students/eligible-for-interval', payload);
      if (reqId !== fetchRef.current) return;

      const defSrv = r.data?.default_students || [];
      const othSrv = r.data?.others || [];

      const defMap = new Map(defSrv.map(s => [String(s.id), s]));
      const defaultsLocal = (allStudents || []).filter(s => String(s.default_teacher_id) === String(teacherId));

      const defList = defaultsLocal.map(s => {
        const serverHit = defMap.get(String(s.id));
        if (!serverHit) {
          return {
            student: { ...s, program_codes: s.program_codes || s.disciplines || [] },
            ok: false,
            reason: 'Not available in this interval.',
          };
        }
        return {
          student: {
            ...s,
            ...serverHit,
            program_codes: Array.isArray(serverHit.program_codes)
              ? serverHit.program_codes
              : (Array.isArray(serverHit.disciplines) ? serverHit.disciplines : []),
            taken_program_codes: Array.isArray(serverHit.taken_program_codes) ? serverHit.taken_program_codes : [],
            telehealth_available: !!serverHit.telehealth_available,
            telehealth_label: serverHit.telehealth_label || 'Telehealth',
          },
          ok: true,
          reason: '',
        };
      });

      const oth = othSrv.map(o => {
        const full = (allStudents || []).find(s => String(s.id) === String(o.id)) || {};
        return {
          ...full,
          ...o,
          program_codes: Array.isArray(o.program_codes) ? o.program_codes : (Array.isArray(o.disciplines) ? o.disciplines : []),
          taken_program_codes: Array.isArray(o.taken_program_codes) ? o.taken_program_codes : [],
          telehealth_available: !!o.telehealth_available,
          telehealth_label: o.telehealth_label || 'Telehealth',
        };
      });

      // sort default: ok first, then distance/name
      const nameKey = (x) => `${String(x?.last_name || '').toLowerCase()}|${String(x?.first_name || '').toLowerCase()}|${x?.id}`;
      const distKey = (x) => {
        const mode = String(distanceMode || '').toLowerCase();
        if (mode === 'ors') {
          const rmi = x?.route_mi != null ? Number(x.route_mi) : null;
          const sli = x?.straight_line_mi != null ? Number(x.straight_line_mi) : null;
          const v = Number.isFinite(rmi) ? rmi : (Number.isFinite(sli) ? sli : Number.POSITIVE_INFINITY);
          return v;
        }
        if (mode === 'haversine') {
          const v = x?.straight_line_mi != null ? Number(x.straight_line_mi) : Number.POSITIVE_INFINITY;
          return Number.isFinite(v) ? v : Number.POSITIVE_INFINITY;
        }
        return Number.POSITIVE_INFINITY;
      };

      const defSorted = defList.slice().sort((a, b) => {
        const aBad = a.ok === false ? 1 : 0;
        const bBad = b.ok === false ? 1 : 0;
        if (aBad !== bBad) return aBad - bBad;

        const da = distKey(a.student);
        const db = distKey(b.student);
        if (da !== db) return da - db;

        return nameKey(a.student).localeCompare(nameKey(b.student));
      });

      const othSorted = oth.slice().sort((a, b) => {
        const da = distKey(a);
        const db = distKey(b);
        if (da !== db) return da - db;
        return nameKey(a).localeCompare(nameKey(b));
      });

      setEligibleDefault(defSorted);
      setOtherEligible(othSorted);

      if (openModalIfClosed) setShowModal(true);
    } catch (err) {
      console.error('eligible-for-interval failed:', err);
      if (!preserveExisting) {
        setEligibleDefault([]);
        setOtherEligible([]);
      }
      if (openModalIfClosed) setShowModal(true);
    } finally {
      if (reqId === fetchRef.current) setLoadingEligible(false);
    }
  }

  /** ✅ Auto-recompute when distance/clinic/origin changes */
  useEffect(() => {
    if (!showModal) return;
    if (!modalTeacherId || !modalDate || !modalFrom || !modalMaxTo) return;

    const clinicKey = (visitLocationMode === 'at_clinic' && selectedClinic?.id)
      ? `clinic:${selectedClinic.id}`
      : 'home';

    const originKey = (() => {
      const ovr = buildOriginOverrideForModalInterval(modalTeacherId, modalDate, modalFrom);
      if (!ovr) return 'no-ovr';
      if (ovr.origin_type === 'org_location') return `ovr:org:${ovr.org_location_id}`;
      return 'ovr:other';
    })();

    const key = [
      String(modalTeacherId),
      String(modalDate),
      String(modalFrom),
      String(modalMaxTo),
      String(distanceMode),
      String(clinicKey),
      String(originKey),
    ].join('|');

    if (key === lastEligibleKeyRef.current) return;

    fetchEligibleForModal({
      teacherId: modalTeacherId,
      date: modalDate,
      from: modalFrom,
      maxTo: modalMaxTo,
      openModalIfClosed: false,
      preserveExisting: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showModal,
    modalTeacherId, modalDate, modalFrom, modalMaxTo,
    distanceMode,
    visitLocationMode, selectedClinic?.id,
    candidatesByTeacher,
  ]);

  /** ---------- Modal: open by click on free segment ---------- */
  async function openModalForTeacherFree(teacherId, seg, e) {
    if (!selectedDate) return;

    if (todayStr && selectedDate < todayStr) {
      setToastText('Past dates are locked. You cannot schedule visits in the past.');
      return;
    }

    const tId = String(teacherId);

    const track = trackRefs.current[tId];
    if (!track) return;

    const rect = track.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;

    const dayStartM = toMinutes(workday.start);
    const dayEndM = toMinutes(workday.end);
    const spanM = Math.max(1, dayEndM - dayStartM);

    const segFromM = toMinutes(seg.from);
    const segToM = toMinutes(seg.to);

    const SNAP_MIN = 5;
    const SLOT_MIN = 45;

    const segLeftPx = rect.left + ((segFromM - dayStartM) / spanM) * rect.width;
    const segRightPx = rect.left + ((segToM - dayStartM) / spanM) * rect.width;
    const segWidthPx = Math.max(1, segRightPx - segLeftPx);
    const localPx = clamp(clientX - segLeftPx, 0, segWidthPx);

    const segSpanM = segToM - segFromM;
    let minuteAtX =
      segFromM + Math.round((localPx / segWidthPx) * (segSpanM / SNAP_MIN)) * SNAP_MIN;

    minuteAtX = clamp(minuteAtX, segFromM, Math.max(segFromM, segToM - SLOT_MIN));
    const start = minutesToHHMM(minuteAtX);

    setModalTeacherId(tId);
    setModalDate(selectedDate);
    setModalFrom(start);
    setModalMaxTo(seg.to);
    setModalAnchor({ from: seg.from, to: seg.to });

    // reset location to at_home each time
    setVisitLocationMode('at_home');
    setSelectedClinic(null);

    await fetchEligibleForModal({
      teacherId: tId,
      date: selectedDate,
      from: start,
      maxTo: seg.to,
      openModalIfClosed: true,
      preserveExisting: false,
    });
  }

  function handleDisciplineSelect(studentId, code) {
    setSelectedDisciplines(prev => ({ ...prev, [studentId]: code }));
  }

  function addCandidateForTeacher({
    teacherId,
    from,
    to,
    student,
    disciplineCode = null,
    deliveryMode = 'in_person',
    telehealthLink = null,
    locationMode = 'at_home',
    orgLocationId = null,
  }) {
    if (todayStr && selectedDate && selectedDate < todayStr) {
      setToastText('Past dates are locked. You cannot schedule visits in the past.');
      return;
    }

    const okRes = canAddCandidate(student?.id, disciplineCode);
    if (!okRes.ok) {
      setToastText(okRes.reason || 'Cannot add candidate.');
      return;
    }

    if (String(locationMode || '').toLowerCase() === 'at_clinic' && !orgLocationId) {
      setToastText('Select clinic location first (At clinic).');
      return;
    }

    const anchorFromM = toMinutes(modalAnchor.from);
    const anchorToM = toMinutes(modalAnchor.to);
    const fM = toMinutes(from);
    const tM = toMinutes(to);
    if (fM < anchorFromM || tM > anchorToM || tM <= fM) {
      setToastText('Selected time does not fit within free window.');
      return;
    }

    const existing = (candidatesByTeacher[String(teacherId)] || []);
    const overlapsAny = existing.some(c => overlaps(from, to, c.from, c.to));
    if (overlapsAny) {
      setToastText('Overlaps with another candidate for this provider.');
      return;
    }

    const fullStudent = (() => {
      const s = (allStudents || []).find(x => String(x.id) === String(student.id));
      if (!s) return student;
      return { ...s, ...student };
    })();

    // prefetch availability (smooth drag)
    if (selectedDate && fullStudent?.id) ensureStudentAvailability(fullStudent.id, selectedDate);

    setCandidatesByTeacher(prev => {
      const cp = { ...prev };
      const list = (cp[String(teacherId)] || []).slice();
      list.push({
        date: selectedDate,
        from: normalizeTime(from),
        to: normalizeTime(to),
        student: {
          id: fullStudent.id,
          first_name: fullStudent.first_name,
          last_name: fullStudent.last_name,
          name: (fullStudent.last_name || '') + ', ' + (fullStudent.first_name || ''),
          default_teacher_id: fullStudent.default_teacher_id ?? student.default_teacher_id ?? null,
        },
        type: 'standard',
        discipline_code: disciplineCode,
        delivery_mode: deliveryMode,
        telehealth_link: telehealthLink,
        location_mode: locationMode || 'at_home',
        org_location_id: orgLocationId || null,
      });
      cp[String(teacherId)] = list;
      return cp;
    });

    setShowModal(false);
  }

  async function selectStudentWithDiscipline(
    student,
    programCode,
    deliveryMode = 'in_person',
    telehealthLink = null,
    locationMode = 'at_home',
    orgLocationId = null
  ) {
    const baseDuration = getDisciplineDurationEffective(programCode, 'standard', deliveryMode);
    const end = minutesToHHMM(Math.min(toMinutes(modalFrom) + baseDuration, toMinutes(modalMaxTo)));

    addCandidateForTeacher({
      teacherId: modalTeacherId,
      from: modalFrom,
      to: end,
      student,
      disciplineCode: programCode || null,
      deliveryMode,
      telehealthLink,
      locationMode,
      orgLocationId,
    });
  }

  function openTelehealthModal(student, disciplineCode) {
    setTeleStudent(student);
    setTeleDisciplineCode(disciplineCode);
    setTeleLink('');
    setTeleError('');

    setTeleVisitLocationMode(visitLocationMode);
    setTeleOrgLocationId((visitLocationMode === 'at_clinic' && selectedClinic?.id) ? Number(selectedClinic.id) : null);

    setShowModal(false);
    setShowTeleModal(true);
  }

  async function handleTelehealthSave() {
    if (!teleStudent) return setTeleError('Missing student.');
    if (!teleDisciplineCode) return setTeleError('Missing discipline.');
    if (!teleLink || !teleLink.trim()) return setTeleError('Please enter telehealth link.');

    try {
      await selectStudentWithDiscipline(
        teleStudent,
        teleDisciplineCode,
        'telehealth',
        teleLink.trim(),
        teleVisitLocationMode,
        teleOrgLocationId
      );
      setShowTeleModal(false);
      setTeleStudent(null);
      setTeleDisciplineCode(null);
      setTeleLink('');
      setTeleError('');
    } catch {
      setTeleError('Failed to add telehealth visit.');
    }
  }

  function cancelTelehealthModal() {
    setShowTeleModal(false);
    setTeleStudent(null);
    setTeleDisciplineCode(null);
    setTeleLink('');
    setTeleError('');
    if (modalDate && modalFrom && modalMaxTo) setShowModal(true);
  }

  /** ---------- Timeline calc vars ---------- */
  const DAY_START = workday.start;
  const DAY_END = workday.end;

  const safeStart = (toMinutes(DAY_END) <= toMinutes(DAY_START)) ? '08:00' : DAY_START;
  const safeEnd = (toMinutes(DAY_END) <= toMinutes(DAY_START)) ? '18:00' : DAY_END;

  const dayStartM = toMinutes(safeStart);
  const dayEndM = toMinutes(safeEnd);
  const spanM = Math.max(1, dayEndM - dayStartM);

  /** ---------- Validate candidate move/resize vs free/busy/candidates ---------- */
  function isWithinSomeFreeSegment(teacherId, from, to) {
    const pack = dayPlanner?.byTeacher?.[String(teacherId)] || {};
    const free = Array.isArray(pack.free) ? pack.free : [];
    return free.some(seg => {
      const sf = toMinutes(seg.from);
      const st = toMinutes(seg.to);
      const f = toMinutes(from);
      const t = toMinutes(to);
      return f >= sf && t <= st;
    });
  }

  function overlapsBusy(teacherId, from, to) {
    const pack = dayPlanner?.byTeacher?.[String(teacherId)] || {};
    const busy = Array.isArray(pack.busy) ? pack.busy : [];
    return busy
      .filter(v => String(v.status) !== 'canceled')
      .some(v => {
        const { from: bf, to: bt } = getBusyFromTo(v);
        if (!bf || !bt) return false;
        return overlaps(from, to, bf, bt);
      });
  }

  function overlapsOtherCandidates(teacherId, idx, from, to) {
    const list = Array.isArray(candidatesByTeacher?.[String(teacherId)]) ? candidatesByTeacher[String(teacherId)] : [];
    return list.some((c, j) => {
      if (j === idx) return false;
      return overlaps(from, to, c.from, c.to);
    });
  }

  function getMinMaxForCandidate(c) {
    if (isWorkCandidate(c)) return { min: 30, max: 240 };
    return getMinMaxDurationEffective(c.discipline_code, c.type || 'standard', c.delivery_mode || 'in_person');
  }
  function isHardLimitedForCandidate(c) {
    if (isWorkCandidate(c)) return false;
    return isHardLimitedEffective(c.discipline_code, c.type || 'standard', c.delivery_mode || 'in_person');
  }

  /** ✅ Prefetch availability for candidates (like Teacher) */
  useEffect(() => {
    if (!selectedDate) return;
    const tids = Object.keys(candidatesByTeacher || {});
    const ids = new Set();
    for (const tid of tids) {
      const list = Array.isArray(candidatesByTeacher[tid]) ? candidatesByTeacher[tid] : [];
      list.forEach(c => {
        if (isWorkCandidate(c)) return;
        const sid = c?.student?.id;
        if (sid) ids.add(Number(sid));
      });
    }
    ids.forEach(sid => ensureStudentAvailability(sid, selectedDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, candidatesByTeacher]);

  /** ---------- Drag/Resize handlers (candidates) ---------- */
  const SNAP_MIN = 5;

  function startDragCandidate(e, teacherId, idx, mode = 'move') {
    e.preventDefault();
    e.stopPropagation();

    const tid = String(teacherId);
    const list = candidatesByTeacher[tid] || [];
    const c = list[idx];
    if (!c) return;

    if (mode !== 'move') {
      const hard = isHardLimitedForCandidate(c);
      if (hard) return;
    }

    const startX = e.clientX ?? (e.touches?.[0]?.clientX);
    if (startX == null) return;

    const fromM = toMinutes(c.from);
    const toM = toMinutes(c.to);

    const isWorkCand = isWorkCandidate(c);
    const sid = (!isWorkCand && c?.student?.id) ? Number(c.student.id) : null;
    const dateStr = selectedDate;

    let availBounds = null;
    let needsLoad = false;

    // availability only for student visit candidates
    if (sid && dateStr) {
      const key = makeAvailKey(dateStr, sid);
      const cached = studentAvailability[key];
      if (cached && cached.length) {
        availBounds = getAvailabilityBoundsForCandidate(cached, fromM, toM);
      } else {
        needsLoad = true;
        ensureStudentAvailability(sid, dateStr);
        setToastText('Loading student availability…');
      }
    }

    setDrag({
      teacherId: tid,
      idx,
      mode,
      startX,
      startFromM: fromM,
      startToM: toM,
      lastGood: { fromM, toM },
      studentId: sid,
      date: dateStr,
      availBounds,
      availLoading: needsLoad,
    });
  }

  useEffect(() => {
    if (!drag) return;

    const tid = drag.teacherId;
    const track = trackRefs.current[tid];
    if (!track) return;

    const onMove = (ev) => {
      const x = ev.clientX ?? (ev.touches?.[0]?.clientX);
      if (x == null) return;

      // block until availability ready if we need it
      let availBounds = drag.availBounds;
      if ((!availBounds) && drag.studentId && drag.date) {
        const key = makeAvailKey(drag.date, drag.studentId);
        const cached = studentAvailability[key];
        if (cached && cached.length) {
          availBounds = getAvailabilityBoundsForCandidate(cached, drag.startFromM, drag.startToM);
        }
      }
      if (drag.studentId && drag.date && drag.availLoading && !availBounds) return;

      const rect = track.getBoundingClientRect();
      const pxPerMin = rect.width / spanM;
      const dx = x - drag.startX;
      const rawDeltaMin = pxPerMin > 0 ? dx / pxPerMin : 0;
      const deltaMin = Math.round(rawDeltaMin / SNAP_MIN) * SNAP_MIN;

      let newFromM = drag.startFromM;
      let newToM = drag.startToM;

      if (drag.mode === 'move') {
        newFromM = drag.startFromM + deltaMin;
        newToM = drag.startToM + deltaMin;
      } else if (drag.mode === 'resize-l') {
        newFromM = drag.startFromM + deltaMin;
        newToM = drag.startToM;
      } else if (drag.mode === 'resize-r') {
        newFromM = drag.startFromM;
        newToM = drag.startToM + deltaMin;
      }

      // day clamp
      newFromM = clamp(newFromM, dayStartM, dayEndM - 1);
      newToM = clamp(newToM, dayStartM + 1, dayEndM);
      if (newToM <= newFromM) return;

      const list = candidatesByTeacher[tid] || [];
      const c = list[drag.idx];
      if (!c) return;

      const { min, max } = getMinMaxForCandidate(c);
      let dur = newToM - newFromM;

      if (dur < min) {
        if (drag.mode === 'resize-l') newFromM = newToM - min;
        else newToM = newFromM + min;
      }
      if (dur > max) {
        if (drag.mode === 'resize-l') newFromM = newToM - max;
        else newToM = newFromM + max;
      }

      // re-clamp after duration adjustments
      if (newFromM < dayStartM) {
        const shift = dayStartM - newFromM;
        newFromM += shift;
        newToM += shift;
      }
      if (newToM > dayEndM) {
        const shift = newToM - dayEndM;
        newFromM -= shift;
        newToM -= shift;
      }
      if (newToM <= newFromM) return;

      // ✅ availability clamp (student candidates only)
      if (availBounds) {
        const minFrom = availBounds.fromM;
        const maxTo = availBounds.toM;

        if (drag.mode === 'move') {
          dur = newToM - newFromM;
          const lo = minFrom;
          const hi = maxTo - dur;
          newFromM = clamp(newFromM, lo, hi);
          newToM = newFromM + dur;
        } else if (drag.mode === 'resize-l') {
          const upper = Math.min(newToM - min, newToM - 1);
          const lower = Math.max(minFrom, newToM - max);
          newFromM = clamp(newFromM, lower, upper);
        } else if (drag.mode === 'resize-r') {
          const lower = Math.max(newFromM + min, newFromM + 1);
          const upper = Math.min(maxTo, newFromM + max);
          newToM = clamp(newToM, lower, upper);
        }

        newFromM = clamp(newFromM, dayStartM, dayEndM - 1);
        newToM = clamp(newToM, dayStartM + 1, dayEndM);
        if (newToM <= newFromM) return;
      }

      const from = minutesToHHMM(newFromM);
      const to = minutesToHHMM(newToM);

      if (!isWithinSomeFreeSegment(tid, from, to)) return;
      if (overlapsBusy(tid, from, to)) return;
      if (overlapsOtherCandidates(tid, drag.idx, from, to)) return;

      setCandidatesByTeacher(prev => {
        const cp = { ...prev };
        const arr = (cp[tid] || []).slice();
        const cur = arr[drag.idx];
        if (!cur) return prev;
        arr[drag.idx] = { ...cur, from, to };
        cp[tid] = arr;
        return cp;
      });

      setDrag(prev => prev ? { ...prev, lastGood: { fromM: newFromM, toM: newToM }, availBounds, availLoading: false } : prev);
    };

    const onUp = () => setDrag(null);

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, spanM, dayStartM, dayEndM, candidatesByTeacher, dayPlanner, studentAvailability]);

  /** ---------- UI ---------- */
  const rightPanelStyle = (isLg && calendarHeight)
    ? { height: `${calendarHeight}px` }
    : undefined;

  /** Work modal derived limits */
  const workCountForDay = useMemo(() => {
    if (!modalTeacherId) return 0;

    const bt = dayPlanner?.byTeacher?.[String(modalTeacherId)];
    const busy = Array.isArray(bt?.busy) ? bt.busy : [];
    const savedCnt = busy.filter(b =>
      isWorkBlock(b) && String(b?.status) !== 'canceled'
    ).length;

    const candList = Array.isArray(candidatesByTeacher?.[String(modalTeacherId)])
      ? candidatesByTeacher[String(modalTeacherId)]
      : [];
    const candCnt = candList.filter(c => isWorkCandidate(c)).length;

    return savedCnt + candCnt;
  }, [dayPlanner, modalTeacherId, candidatesByTeacher]);

  const workMaxMinutes = useMemo(() => {
    let maxM = 240;
    if (modalFrom && modalAnchor?.to) {
      const span = toMinutes(modalAnchor.to) - toMinutes(modalFrom);
      if (Number.isFinite(span) && span > 0) maxM = Math.max(30, Math.min(240, span));
    }
    return maxM;
  }, [modalFrom, modalAnchor]);
  
  function computeWorkMaxM(modalFrom, modalAnchorTo) {
    const span = (modalFrom && modalAnchorTo) ? (toMinutes(modalAnchorTo) - toMinutes(modalFrom)) : null;
    return (Number.isFinite(span) && span > 0)
      ? Math.max(30, Math.min(240, span))
      : 240;
  }
  function normalizePlannedMinutes(input, maxM) {
    let mins = Number(input);
    if (!Number.isFinite(mins)) mins = 60;
    return Math.max(30, Math.min(maxM, mins));
  }

  /** -------- Work modal helpers -------- */
  function openWorkModal() {
    if (!modalDate || !modalFrom || !modalTeacherId) {
      setToastText('Missing modal context (date/from/teacher).');
      return;
    }

    const bt = dayPlanner?.byTeacher?.[String(modalTeacherId)];
    const busy = Array.isArray(bt?.busy) ? bt.busy : [];
    const existingWorkSaved = busy.filter(b => isWorkBlock(b) && String(b?.status) !== 'canceled');

    const existingWorkCand = (Array.isArray(candidatesByTeacher?.[String(modalTeacherId)])
      ? candidatesByTeacher[String(modalTeacherId)]
      : []
    ).filter(c => isWorkCandidate(c));

    if ((existingWorkSaved.length + existingWorkCand.length) >= 6) {
      setToastText('Max 6 work blocks per day already reached.');
      return;
    }

    if (!orgLocations.length && !orgLocationsLoading) {
      loadOrgLocations();
    }

    const maxM = computeWorkMaxM(modalFrom, modalAnchor?.to);
	setWorkForm(prev => ({ ...prev, planned_minutes: normalizePlannedMinutes(prev.planned_minutes, maxM) }));

    setWorkModalOpen(true);
	setShowModal(false);
  }

  function closeWorkModal() {
    setWorkModalOpen(false);
	setShowModal(true);
  }

  function parseWorkLocation(value) {
    const v = String(value || '').trim();
    if (!v) return { location_mode: null, org_location_id: null };
    if (v === 'teacher_home') return { location_mode: 'teacher_home', org_location_id: null };
    if (v === 'remote') return { location_mode: 'remote', org_location_id: null };
    if (v.startsWith('org:')) return { location_mode: 'org_location', org_location_id: Number(v.slice(4)) || null };
    return { location_mode: null, org_location_id: null };
  }

  function addWorkCandidateForTeacher({
    teacherId,
    from,
    to,
    work_type,
    title,
    notes,
    location_mode,
    org_location_id,
  }) {
    if (todayStr && selectedDate && selectedDate < todayStr) {
      setToastText('Past dates are locked. You cannot schedule in the past.');
      return false;
    }

    const tid = String(teacherId);
    const fM = toMinutes(from);
    const tM = toMinutes(to);

    const anchorFromM = toMinutes(modalAnchor.from);
    const anchorToM = toMinutes(modalAnchor.to);
    if (fM < anchorFromM || tM > anchorToM || tM <= fM) {
      setToastText('Selected work time does not fit within free window.');
      return false;
    }

    // overlap vs saved busy (visits + work)
    if (overlapsBusy(tid, from, to)) {
      setToastText('Work overlaps with an existing saved item.');
      return false;
    }

    // overlap vs other candidates (visits + work)
    if (overlapsOtherCandidates(tid, -1, from, to)) {
      setToastText('Work overlaps with another candidate.');
      return false;
    }

    // must be within a free segment
    if (!isWithinSomeFreeSegment(tid, from, to)) {
      setToastText('Work must be inside a free segment.');
      return false;
    }

    // max 2/day: saved + candidates
    const bt = dayPlanner?.byTeacher?.[tid] || {};
    const busy = Array.isArray(bt?.busy) ? bt.busy : [];
    const savedCnt = busy.filter(b => isWorkBlock(b) && String(b?.status) !== 'canceled').length;

    const candList = Array.isArray(candidatesByTeacher?.[tid]) ? candidatesByTeacher[tid] : [];
    const candCnt = candList.filter(c => isWorkCandidate(c)).length;

    if ((savedCnt + candCnt) >= 6) {
      setToastText('Max 6 work blocks per day reached.');
      return false;
    }

    setCandidatesByTeacher(prev => {
      const cp = { ...prev };
      const list = (cp[tid] || []).slice();
      list.push({
        candidate_kind: 'work',
        date: selectedDate,
        from: normalizeTime(from),
        to: normalizeTime(to),

        work_type: String(work_type),
        title: (title || '').toString().slice(0, 160) || null,
        notes: (notes || '').toString() || null,

        location_mode: String(location_mode),
        org_location_id: String(location_mode) === 'org_location'
          ? (org_location_id ? Number(org_location_id) : null)
          : null,
      });
      cp[tid] = list;
      return cp;
    });

    return true;
  }

  async function submitWork() {
    if (workSubmitting) return;

    if (!modalDate || !modalFrom || !modalTeacherId) {
      setToastText('Missing modal context (date/from/teacher).');
      return;
    }

    const { location_value, work_type, planned_minutes, title, notes } = workForm || {};
    const { location_mode, org_location_id } = parseWorkLocation(location_value);

    if (!location_mode) {
      setToastText('Please select a work location.');
      return;
    }
    if (location_mode === 'org_location' && !org_location_id) {
      setToastText('Please select an organization location.');
      return;
    }

    const allowedTypes = ['admin', 'documentation', 'training', 'research', 'meeting', 'other'];
    if (!allowedTypes.includes(String(work_type))) {
      setToastText('Please select a work type.');
      return;
    }

    const maxM = computeWorkMaxM(modalFrom, modalAnchor?.to);
	const mins = normalizePlannedMinutes(planned_minutes, maxM);

    const from = normalizeTime(modalFrom);
    const to = addMinutesHHMM(from, mins);

    setWorkSubmitting(true);
    try {
      const ok = addWorkCandidateForTeacher({
        teacherId: modalTeacherId,
        from,
        to,
        work_type,
        title,
        notes,
        location_mode,
        org_location_id,
      });

      if (!ok) return;

      setToastText('Work candidate added.');
      setWorkModalOpen(false);

      // refresh eligible list if still open (busy changed on client side)
      if (showModal && modalTeacherId && modalDate && modalFrom && modalMaxTo) {
        fetchEligibleForModal({
          teacherId: modalTeacherId,
          date: modalDate,
          from: modalFrom,
          maxTo: modalMaxTo,
          openModalIfClosed: false,
          preserveExisting: true,
        });
      }
    } finally {
      setWorkSubmitting(false);
    }
  }

  function removeCandidate(teacherId, idx) {
    setCandidatesByTeacher(prev => {
      const cp = { ...prev };
      const list = (cp[String(teacherId)] || []).slice();
      list.splice(idx, 1);
      cp[String(teacherId)] = list;
      return cp;
    });
  }

  /** ---------- DELETE / CANCEL helpers ---------- */
  async function deleteVisitById(visitId) {
    const u = getUserFromStorage();
    const isSuper = u?.role === 'super_admin';
    const orgId = isSuper ? getActiveOrganizationIdForSuperAdmin() : null;
    const cfg = (isSuper && orgId) ? { params: { organization_id: orgId } } : undefined;
    await API.delete(`/visits/${visitId}`, cfg);
    return true;
  }
  async function deleteWorkById(workId) {
    const u = getUserFromStorage();
    const isSuper = u?.role === 'super_admin';
    const orgId = isSuper ? getActiveOrganizationIdForSuperAdmin() : null;
    const cfg = (isSuper && orgId) ? { params: { organization_id: orgId } } : undefined;
    await API.delete(`/teacher-work/${workId}`, cfg);
    return true;
  }
  async function cancelVisitById(visitId, reason = '') {
    const u = getUserFromStorage();
    const isSuper = u?.role === 'super_admin';
    const orgId = isSuper ? getActiveOrganizationIdForSuperAdmin() : null;
    const cfg = (isSuper && orgId) ? { params: { organization_id: orgId } } : undefined;
    await API.post(`/admin/visits/${visitId}/cancel`, { reason: (reason || '').toString() }, cfg);
    return true;
  }
  async function cancelWorkById(workId, reason = '') {
    const u = getUserFromStorage();
    const isSuper = u?.role === 'super_admin';
    const orgId = isSuper ? getActiveOrganizationIdForSuperAdmin() : null;
    const cfg = (isSuper && orgId) ? { params: { organization_id: orgId } } : undefined;
    await API.post(`/teacher-work/${workId}/cancel`, { reason: (reason || '').toString() }, cfg);
    return true;
  }

  async function refreshMonthVisits() {
    if (!monthCursor) return;
    const y = monthCursor.getFullYear();
    const m = monthCursor.getMonth() + 1;
    const from = `${y}-${pad2(m)}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const to = `${y}-${pad2(m)}-${pad2(lastDay)}`;
    const r2 = await API.get('/admin/visits-calendar', { params: { date_from: from, date_to: to } });
    setVisits(r2.data || []);
  }

  function visitDateOf(v) {
    const isWork = isWorkBlock(v);
    const d = isWork ? getWorkDate(v, selectedDate) : (
      (v?.scheduled_at ? String(v.scheduled_at).slice(0, 10) : null) ||
      (v?.date ? String(v.date).slice(0, 10) : null) ||
      selectedDate ||
      null
    );
    return d;
  }

  function isLockedSaved(v) {
    const st = String(v?.status || '');
    if (st !== 'draft') return true;
    const d = visitDateOf(v);
    if (todayStr && d && d < todayStr) return true;
    return false;
  }

  function isCancelableSaved(v) {
    const st = String(v?.status || '');
    if (st === 'canceled') return false;
    if (st === 'complete') return false;
    const d = visitDateOf(v);
    if (todayStr && d && d < todayStr) return false;
    return true;
  }

  async function handleDeleteSavedVisit(v) {
    const isWork = isWorkBlock(v);
    const id = isWork ? getWorkId(v) : (v?.visit_id || v?.id);

    if (!id) {
      setToastText(isWork ? 'Missing work id.' : 'Missing visit_id.');
      return;
    }
    if (isLockedSaved(v)) {
      setToastText(isWork ? 'This work block is locked and cannot be deleted.' : 'This visit is locked and cannot be deleted.');
      return;
    }
    if (!window.confirm(isWork ? 'Delete this draft work block?' : 'Delete this draft visit?')) return;

    try {
      if (isWork) await deleteWorkById(id);
      else await deleteVisitById(id);

      setToastText('Deleted.');
      await loadDayPlanner();
      await refreshMonthVisits();
    } catch (e) {
      setToastText(e?.response?.data?.error || 'Delete failed.');
    }
  }

  async function handleCancelSavedVisit(v) {
    const isWork = isWorkBlock(v);
    const id = isWork ? getWorkId(v) : (v?.visit_id || v?.id);

    if (!id) {
      setToastText(isWork ? 'Missing work id.' : 'Missing visit_id.');
      return;
    }
    if (!isCancelableSaved(v)) {
      setToastText(isWork ? 'This work block cannot be canceled.' : 'This visit cannot be canceled.');
      return;
    }

    setCancelTarget(v);
    setCancelMode(isWork ? 'work' : 'visit');
    setReason('');
    setShowCancelModal(true);
  }

  async function confirmCancelSavedVisit() {
    if (!cancelTarget) return;

    const isWork = cancelMode === 'work' || isWorkBlock(cancelTarget);
    const id = isWork ? getWorkId(cancelTarget) : (cancelTarget?.visit_id || cancelTarget?.id);

    if (!id) {
      setToastText(isWork ? 'Missing work id.' : 'Missing visit_id.');
      return;
    }
    if (!String(reason || '').trim()) {
      setToastText(isWork ? 'Please enter a short reason.' : 'Please select a cancel reason.');
      return;
    }

    setCancelSubmitting(true);
    try {
      if (isWork) await cancelWorkById(id, reason);
      else await cancelVisitById(id, reason);

      setToastText('Canceled.');
      setShowCancelModal(false);
      setCancelTarget(null);
      setCancelMode('visit');
      setReason('');

      await loadDayPlanner();
      await refreshMonthVisits();
    } catch (e) {
      setToastText(e?.response?.data?.error || 'Cancel failed.');
    } finally {
      setCancelSubmitting(false);
    }
  }

  function closeCancelModal() {
    if (cancelSubmitting) return;
    setShowCancelModal(false);
    setCancelTarget(null);
    setCancelMode('visit');
    setReason('');
  }

  /** Save (Selected Day) => /api/visits/bulk + /api/teacher-work/bulk */
  async function saveSelectedDay() {
    if (!selectedDate) return;

    if (todayStr && selectedDate < todayStr) {
      setToastText('Past dates are locked. You cannot schedule visits in the past.');
      return;
    }

    const visitsPayload = [];
    const workPayload = [];

    const teacherIds = Object.keys(candidatesByTeacher || {});
    for (const tid of teacherIds) {
      const list = candidatesByTeacher[tid] || [];
      for (const c of list) {
        if (isWorkCandidate(c)) {
          const from = normalizeTime(c.from);
          const to = normalizeTime(c.to);
          const mins = Math.max(1, toMinutes(to) - toMinutes(from));

          workPayload.push({
            teacher_id: Number(tid),
            scheduled_at: `${selectedDate} ${from}`,
            planned_minutes: mins,
            end_time: to,
            status: 'draft',
            work_type: String(c.work_type || 'other'),
            title: (c.title || '').toString().slice(0, 160) || null,
            notes: (c.notes || '').toString() || null,
            location_mode: String(c.location_mode || ''),
            org_location_id: String(c.location_mode) === 'org_location'
              ? (c.org_location_id ? Number(c.org_location_id) : null)
              : null,
          });
          continue;
        }

        const studentDefaultTid =
          c.student?.default_teacher_id ??
          (allStudents.find(s => String(s.id) === String(c.student?.id))?.default_teacher_id ?? null);

        const isDefaultForThisTeacher = String(studentDefaultTid || '') === String(tid);

        const original_tid = isDefaultForThisTeacher
          ? Number(tid)
          : (studentDefaultTid ? Number(studentDefaultTid) : Number(tid));

        const reassigned_status = isDefaultForThisTeacher
          ? 'none'
          : (studentDefaultTid ? 'pending' : 'none');

        visitsPayload.push({
          student_id: Number(c.student.id),
          teacher_id: Number(tid),
          scheduled_at: `${selectedDate} ${normalizeTime(c.from)}`,
          notes: '',
          visit_id: null,
          status: 'draft',
          type: 'standard',
          event_end_time: normalizeTime(c.to),
          original_teacher_id: original_tid,
          reassigned_status,
          discipline_code: c.discipline_code || null,
          delivery_mode: c.delivery_mode || 'in_person',
          telehealth_link: c.telehealth_link || null,

          location_mode: c.location_mode || 'at_home',
          org_location_id: c.org_location_id || null,
        });
      }
    }

    if (!visitsPayload.length && !workPayload.length) {
      setToastText('Nothing to save for selected day.');
      return;
    }

    try {
      if (visitsPayload.length) {
        const resV = await API.post('/visits/bulk', { visits: visitsPayload });
        if (!resV.data?.success) {
          setToastText('Visits save failed.');
          return;
        }
      }

      if (workPayload.length) {
        const resW = await API.post('/teacher-work/bulk', { work: workPayload });
        const okW = !!resW.data?.success;
        const errs = Array.isArray(resW.data?.errors) ? resW.data.errors : [];
        if (!okW) {
          setToastText(errs[0]?.error || 'Work save failed.');
          return;
        }
      }

      setToastText('Saved.');
      setCandidatesByTeacher({});
      await loadDayPlanner();
      await refreshMonthVisits();
    } catch (e) {
      setToastText(e?.response?.data?.error || 'Save failed.');
    }
  }

  return (
    <div className="w-full px-2 sm:px-4">
      <Toast text={toastText} onClose={() => setToastText('')} />

      <h2 className="text-lg font-bold mb-2">Visits Calendar</h2>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-between mb-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Provider</span>
            <button
              type="button"
              onClick={() => setShowProviderPicker(true)}
              className="text-sm border rounded px-2 py-1 bg-white hover:bg-gray-50 max-w-[260px] truncate"
              title={providerButtonLabel}
            >
              {providerButtonLabel}
            </button>
            <button
              type="button"
              onClick={selectAllProviders}
              className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
              title="Show timelines for all providers"
            >
              All
            </button>
            <button
              type="button"
              onClick={() => { setProviderMode('none'); setSelectedProviderIds([]); }}
              className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
              title="Hide timelines (no provider selected)"
            >
              None
            </button>
            {providerMode === 'selected' && (selectedProviderIds || []).length > 0 ? (
              <button
                type="button"
                onClick={clearProviderSelection}
                className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
                title="Clear selected providers"
              >
                Clear
              </button>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm border rounded px-2 py-1 bg-white"
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="complete">Complete</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>
        </div>

        {/* Month stats */}
        <div className="flex flex-wrap gap-2">
          <StatPill label="Total" value={monthStats.total} tone="slate" />
          <StatPill label="Draft" value={monthStats.draft} tone="blue" />
          <StatPill label="Pending" value={monthStats.pending} tone="yellow" />
          <StatPill label="Complete" value={monthStats.complete} tone="green" />
          <StatPill label="Canceled" value={monthStats.canceled} tone="purple" />
          <StatPill label="Substitutes" value={monthStats.subs} tone="red" />
        </div>
      </div>

      {/* Provider picker */}
      {showProviderPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
          <div className="w-full max-w-xl bg-white rounded-xl shadow-xl border">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div>
                <div className="font-semibold">Select providers for timelines</div>
                <div className="text-xs text-gray-500">
                  Pick one or more providers (or choose All). This controls the Day Timelines section.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowProviderPicker(false)}
                className="px-2 py-1 rounded border hover:bg-gray-50"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                <input
                  value={providerSearch}
                  onChange={(e) => setProviderSearch(e.target.value)}
                  placeholder="Search provider…"
                  className="w-full sm:w-72 border rounded px-3 py-2 text-sm"
                />

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllProviders}
                    className="px-3 py-2 text-sm rounded border hover:bg-gray-50"
                  >
                    All providers
                  </button>
                  <button
                    type="button"
                    onClick={clearProviderSelection}
                    className="px-3 py-2 text-sm rounded border hover:bg-gray-50"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="max-h-[55vh] overflow-auto border rounded">
                {(providerPickerList || []).length === 0 ? (
                  <div className="p-3 text-sm text-gray-600">No matches.</div>
                ) : (
                  <div className="divide-y">
                    {providerPickerList.map((t) => {
                      const checked = (providerMode === 'selected') && (selectedProviderIds || []).includes(String(t.id));
                      return (
                        <label key={t.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleProviderId(String(t.id))}
                            disabled={providerMode === 'all'}
                          />
                          <span className="text-sm">{t.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {providerMode === 'all' ? (
                <div className="text-xs text-gray-500">
                  Currently in <b>All providers</b> mode. Uncheck by clicking <b>None</b> in filters, or pick specific providers.
                </div>
              ) : null}

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowProviderPicker(false)}
                  className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Month nav */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="px-2 py-1 text-sm border rounded hover:bg-gray-100"
          >
            ‹
          </button>
          <span className="font-semibold">{monthLabel}</span>
          <button
            type="button"
            onClick={handleNextMonth}
            className="px-2 py-1 text-sm border rounded hover:bg-gray-100"
          >
            ›
          </button>
        </div>

        <button
          type="button"
          onClick={goToToday}
          disabled={!todayStr}
          className={`px-3 py-1 text-xs sm:text-sm rounded border ${
            todayStr
              ? 'border-blue-500 text-blue-700 hover:bg-blue-50'
              : 'border-gray-300 text-gray-400 cursor-not-allowed'
          }`}
        >
          Today
        </button>
      </div>

      {/* Top 2 blocks */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-stretch">
        {/* Calendar */}
        <div ref={calendarBoxRef} className="lg:w-2/3 bg-white border rounded-lg shadow-sm">
          <div className="grid grid-cols-7 text-xs font-semibold text-center border-b bg-gray-50">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="py-2">
                {d}
              </div>
            ))}
          </div>

          {loading && (
            <div className="p-3 text-center text-gray-500 text-sm">Loading visits...</div>
          )}

          <div className="grid grid-cols-7 gap-px bg-gray-200 text-xs">
            {calendarCells.map((cell, idx) => {
              if (cell === null) return <div key={`empty-${idx}`} className="min-h-[72px] bg-gray-50" />;

              const { dateStr } = cell;
              const dayVisits = grouped[dateStr] || [];
              const isSelected = selectedDate === dateStr;
              const isToday = todayStr === dateStr;

              const hasCanceled = dayVisits.some((v) => v.status === 'canceled');
              const hasComplete = dayVisits.some((v) => v.status === 'complete');
              const hasPending = dayVisits.some((v) => v.status === 'pending');
              const hasDraft = dayVisits.some((v) => v.status === 'draft');

              let ringClass = '';
              if (hasCanceled) ringClass = 'ring-2 ring-purple-400';
              else if (hasComplete) ringClass = 'ring-2 ring-green-400';
              else if (hasPending) ringClass = 'ring-2 ring-yellow-400';
              else if (hasDraft) ringClass = 'ring ring-blue-300';

              const baseBg = dayVisits.length
                ? 'bg-white hover:bg-blue-50 cursor-pointer'
                : 'bg-gray-50 hover:bg-gray-100 cursor-pointer';

              const selectedBg = isSelected ? 'bg-blue-50 border-blue-500' : '';
              const selectedOutline = isSelected ? 'outline outline-2 outline-sky-700 outline-offset-[-2px] z-10' : '';
              const todayText = isToday ? 'text-blue-700 font-bold' : '';

              return (
                <div
                  key={dateStr}
                  onClick={() => setSelectedDate(dateStr)}
                  aria-current={isSelected ? 'date' : undefined}
                  className={`relative min-h-[80px] p-1 border border-gray-200 ${baseBg} ${selectedBg} ${ringClass} ${selectedOutline}`}
                >
                  <div className="flex justify-between items-center">
                    <span className={`text-xs ${todayText}`}>{parseInt(dateStr.slice(-2), 10)}</span>
                    {isToday && (
                      <span className="ml-1 text-[10px] px-1 py-0.5 rounded-full bg-green-100 text-green-700">
                        Today
                      </span>
                    )}
                  </div>

                  <MiniDisciplineDots dayVisits={dayVisits} />

                  {dayVisits.length > 0 && (
                    <div className="mt-1 text-[10px] text-gray-500">
                      {dayVisits.length} visit{dayVisits.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel */}
        <div
          className="lg:w-1/3 bg-white border rounded-lg shadow-sm p-3 flex flex-col"
          style={rightPanelStyle}
        >
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-sm font-semibold">
                {selectedDate ? `Visits on ${selectedDate}` : 'Select a day'}
              </div>

              {selectedDate && todayStr && selectedDate === todayStr ? (
                <span className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                  Today
                </span>
              ) : (
                todayStr && (
                  <button
                    type="button"
                    onClick={goToToday}
                    className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full border border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    Jump to today
                  </button>
                )
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {!selectedDate ? (
              <div className="text-xs text-gray-500 mt-2">
                Click on a day in the calendar to see details.
              </div>
            ) : selectedVisits.length === 0 ? (
              <div className="text-xs text-gray-500 mt-2">
                No visits scheduled for this day.
              </div>
            ) : (
              <div className="space-y-2">
                {selectedVisits
                  .slice()
                  .sort((a, b) => (a.scheduled_at || '').localeCompare(b.scheduled_at || ''))
                  .map((v) => {
                    const eventFlag = isEventVisit(v);
                    const isSub = !!v.original_teacher_id && String(v.teacher_id) !== String(v.original_teacher_id);
                    const studentName =
                      v.student_name ||
                      `${v.last_name || ''}, ${v.first_name || ''}`.trim() ||
                      '—';

                    return (
                      <div key={v.visit_id || v.id || `${v.student_id}-${v.scheduled_at}`} className="border rounded-lg p-2 shadow-sm bg-gray-50">
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-sm">{studentName}</span>

                              {eventFlag ? (
                                <EventBadge isEvent />
                              ) : (
                                <ProgramBadge program_type={v.program_type} discipline_code={v.discipline_code} />
                              )}

                              <TelehealthBadge v={v} />

                              <button
                                onClick={() => openNotesHistoryModal(v.student_id, studentName)}
                                className="ml-1 text-purple-700 hover:text-purple-900"
                                title="Show Notes History"
                              >
                                <FaBookOpen size={14} />
                              </button>
                            </div>

                            <div className="mt-1 text-xs text-gray-600">
                              Provider: {v.teacher_name || '—'}
                              {isSub && <span className="ml-1 text-gray-400">(Substitute)</span>}
                            </div>
                          </div>

                          <div className="text-right">
                            <VisitTime
                              scheduled_at={v.scheduled_at}
                              event_end_time={v.event_end_time}
                              event_end_time_calc={v.event_end_time_calc}
                            />
                          </div>
                        </div>

                        <div className="flex justify-between items-center mt-2">
                          <StatusBadge status={v.status}>{v.status}</StatusBadge>
                          <span className="text-[11px] text-gray-700">
                            G-sign:{' '}
                            {v.g_sign_confirmed ? (
                              <span className="text-green-600">Yes</span>
                            ) : (
                              <span className="text-red-600">No</span>
                            )}
                          </span>
                        </div>

                        {v.status === 'canceled' && v.notes && (
                          <div className="mt-1 text-[11px] text-purple-700 italic">{v.notes}</div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {!isLg && selectedVisits.length > 8 ? (
            <div className="mt-2 text-[11px] text-gray-400">
              Tip: list scrolls inside this panel.
            </div>
          ) : null}
        </div>
      </div>

      {/* Day Timelines + Scheduling */}
      <div className="mt-4 bg-white border rounded-lg shadow-sm">
        <div className="p-3 border-b bg-gray-50 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div>
            <div className="font-semibold">Day Timelines</div>
            <div className="text-xs text-gray-600">
              Workday: <span className="font-mono">{safeStart}–{safeEnd}</span>
              {selectedDate ? (
                <>
                  <span className="ml-2">• Day: <b>{selectedDayName || '—'}</b></span>
                  <span className="ml-2">• Date: <b>{selectedDate}</b></span>
                </>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {providerMode === 'none' ? (
                <span className="text-xs text-gray-600">
                  Select providers (or All) to load timelines.
                </span>
              ) : providerMode === 'all' ? (
                <span className="text-xs px-2 py-1 rounded-full border bg-white">
                  All providers
                </span>
              ) : (
                <div className="flex flex-wrap items-center gap-1">
                  {effectiveTimelineTeacherIds.slice(0, 8).map((tid) => {
                    const tObj = (allTeachers || []).find(t => String(t.id) === String(tid));
                    const label = tObj?.name || `Teacher ${tid}`;
                    return (
                      <span
                        key={tid}
                        className="px-2 py-1 text-xs rounded-full border bg-white text-slate-700"
                        title={label}
                      >
                        {label}
                      </span>
                    );
                  })}
                  {effectiveTimelineTeacherIds.length > 8 ? (
                    <span className="text-xs text-gray-500 ml-1">
                      +{effectiveTimelineTeacherIds.length - 8} more
                    </span>
                  ) : null}
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowProviderPicker(true)}
                className="px-3 py-2 text-xs sm:text-sm rounded border hover:bg-gray-50"
              >
                Pick providers
              </button>
            </div>

            <button
              type="button"
              onClick={saveSelectedDay}
              className="px-3 py-2 text-xs sm:text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              disabled={!selectedDate || isPastSelectedDate}
              title={isPastSelectedDate ? 'Past dates are locked' : 'Save candidates for selected day'}
            >
              Save (Selected Day)
            </button>
          </div>
        </div>

        <div className="p-3">
          {!selectedDate ? (
            <div className="text-sm text-gray-600">Select a date on the calendar to view timelines.</div>
          ) : dayPlannerLoading ? (
            <div className="text-sm text-gray-600">Loading day timelines…</div>
          ) : (
            <div className="space-y-4">
              {effectiveTimelineTeacherIds.map((tid) => {
                const tObj = (allTeachers || []).find(t => String(t.id) === String(tid));
                const teacherName = tObj?.name || dayPlanner?.byTeacher?.[tid]?.teacher_name || `Teacher ${tid}`;

                const pack = dayPlanner?.byTeacher?.[tid] || {};
                const free = Array.isArray(pack.free) ? pack.free : [];
                const busyRaw = Array.isArray(pack.busy) ? pack.busy : [];
                const busy = busyRaw.map(v => ({ ...v, ...getBusyFromTo(v) }));
                const cands = Array.isArray(candidatesByTeacher?.[tid]) ? candidatesByTeacher[tid] : [];
                const isDayOff = !!pack.is_day_off;

                const listItems = [
                  ...busy.map(v => {
                    const isWork = isWorkBlock(v);
                    const title =
                      (v.title && String(v.title).trim()) ||
                      (v.work_type ? `Work: ${String(v.work_type)}` : 'Work block');

                    return {
                      kind: 'saved',
                      key: isWork
                        ? `work-${getWorkId(v) || `${v.from}-${v.to}`}`
                        : `saved-${v.visit_id || v.id || `${v.student_id}-${v.from}-${v.to}`}`,
                      from: v.from,
                      to: v.to,
                      student_name: isWork ? title : (v.student_name || '—'),
                      student_id: isWork ? null : v.student_id,
                      discipline_code: isWork ? null : (v.discipline_code || v.program_type || null),
                      type: isWork ? 'work' : (v.type || 'standard'),
                      delivery_mode: v.delivery_mode || 'in_person',
                      telehealth_link: v.telehealth_link || null,
                      status: v.status || 'draft',
                      visit_id: isWork ? null : (v.visit_id || v.id || null),
                      work_id: isWork ? getWorkId(v) : null,
                      raw: v,
                    };
                  }),
                  ...cands.map((c, idx) => {
                    const workCand = isWorkCandidate(c);
                    const workTitle =
                      (c.title && String(c.title).trim()) ||
                      (c.work_type ? `Work: ${String(c.work_type)}` : 'Work block');

                    return {
                      kind: 'candidate',
                      key: `cand-${idx}`,
                      idx,
                      from: c.from,
                      to: c.to,
                      student_name: workCand ? workTitle : (c.student?.name || '—'),
                      discipline_code: workCand ? null : (c.discipline_code || null),
                      type: workCand ? 'work' : (c.type || 'standard'),
                      delivery_mode: workCand ? 'in_person' : (c.delivery_mode || 'in_person'),
                      telehealth_link: workCand ? null : (c.telehealth_link || null),
                      student_id: workCand ? null : c.student?.id,
                      raw: c,
                    };
                  }),
                ].filter(x => x.from && x.to)
                  .sort((a, b) => (a.from || '').localeCompare(b.from || ''));

                return (
                  <div key={tid} className="border rounded p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="font-semibold">{teacherName}</div>
                      <div className="text-xs text-gray-600 flex items-center gap-2">
                        {isDayOff ? (
                          <span className="px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700">
                            Day Off
                          </span>
                        ) : (
                          <>
                            <span className="px-2 py-1 rounded bg-blue-50 border border-blue-200 text-blue-800">
                              Free {free.length}
                            </span>
                            <span className="px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800">
                              Saved {busy.length}
                            </span>
                            {!!cands.length && (
                              <span className="px-2 py-1 rounded bg-gray-50 border border-gray-200 text-gray-700">
                                Candidates {cands.length}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    <div
                      className="relative h-16 rounded bg-gray-100 border select-none overflow-visible"
                      ref={el => (trackRefs.current[String(tid)] = el)}
                    >
                      {/* hourly ticks */}
                      {Array.from({ length: Math.floor(spanM / 60) + 1 }).map((_, i) => {
                        const leftPct = (i * 60) / spanM * 100;
                        return (
                          <div key={i}>
                            <div
                              className="absolute top-4 bottom-0 border-l border-gray-300"
                              style={{ left: `${leftPct}%` }}
                            />
                            <div
                              className="absolute top-0 text-[10px] text-gray-500 -translate-x-1/2"
                              style={{ left: `${leftPct}%` }}
                            >
                              {minutesToHHMM(dayStartM + i * 60).slice(0, 2)}
                            </div>
                          </div>
                        );
                      })}

                      {/* free segments */}
                      {free.map((seg, idx) => {
                        const box = pctBox(seg.from, seg.to, dayStartM, spanM);
                        const disabled = isDayOff || isPastSelectedDate;

                        return (
                          <button
                            key={`f-${idx}`}
                            type="button"
                            className={
                              'absolute top-4 bottom-1 bg-blue-300 rounded z-10 transition-opacity ' +
                              (disabled
                                ? 'opacity-10 cursor-not-allowed'
                                : 'opacity-25 hover:opacity-40 active:opacity-45')
                            }
                            style={{ left: box.left, width: box.width }}
                            title={
                              disabled
                                ? (isPastSelectedDate ? 'Past date (locked)' : 'Day Off')
                                : `Free ${seg.from}–${seg.to}`
                            }
                            onClick={(ev) => openModalForTeacherFree(tid, seg, ev)}
                            disabled={disabled}
                          />
                        );
                      })}

                      {/* ✅ Student availability highlight for candidates (student candidates only) */}
                      {cands.map((c, idx) => {
                        if (isWorkCandidate(c)) return null;

                        const sid = c?.student?.id;
                        if (!selectedDate || !sid) return null;

                        const key = makeAvailKey(selectedDate, sid);
                        const entry = studentAvailability[key];
                        if (!entry || !Array.isArray(entry)) return null;

                        const bounds = getAvailabilityBoundsForCandidate(entry, toMinutes(c.from), toMinutes(c.to));
                        if (!bounds) return null;

                        const isActive = !!drag && String(drag.teacherId) === String(tid) && Number(drag.idx) === Number(idx);
                        const box = pctBoxM(bounds.fromM, bounds.toM, dayStartM, spanM);

                        return (
                          <div
                            key={`avail-${idx}`}
                            className="absolute top-4 bottom-1 bg-orange-300 rounded pointer-events-none"
                            style={{
                              left: box.left,
                              width: box.width,
                              opacity: isActive ? 0.45 : 0.30,
                              zIndex: 12,
                            }}
                            title="Student availability window"
                          />
                        );
                      })}

                      {/* saved busy */}
                      {busy.map((v, idx) => {
                        const from = normalizeTime(v.from);
                        const to = normalizeTime(v.to);
                        if (!from || !to) return null;
                        const box = pctBox(from, to, dayStartM, spanM);

                        const isCanceled = String(v.status) === 'canceled';
                        const isComplete = String(v.status) === 'complete';

                        const containerCls = isCanceled
                          ? 'border border-gray-300 bg-gray-50 opacity-60'
                          : isComplete
                            ? 'border-gray-400 bg-gray-100'
                            : 'border-emerald-700 bg-white';

                        const fillCls = isCanceled ? 'bg-gray-200 opacity-50' : 'bg-emerald-400 opacity-30';
                        const zCls = isCanceled ? 'z-5' : 'z-30';

                        const titleTxt = isWorkBlock(v) ? (v.title || `Work: ${v.work_type || 'work'}`) : (v.student_name || '');

                        return (
                          <div
                            key={`b-${idx}`}
                            className={`absolute top-5 bottom-2 rounded shadow-sm overflow-hidden ${containerCls} ${zCls}`}
                            style={{ left: box.left, width: box.width }}
                            title={`${from}–${to} • ${titleTxt}`}
                          >
                            <div className={`h-full ${fillCls}`} />
                            <div className="absolute inset-0 flex items-center justify-between px-2 text-xs gap-1">
                              <span className="font-mono">{from}–{to}</span>
                              <span className="hidden sm:inline font-semibold">
                                {isWorkBlock(v) ? (v.title || `Work: ${v.work_type || 'work'}`) : (v.student_name || '—')}
                              </span>
                              <div className="flex items-center gap-1">
                                {isWorkBlock(v) ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200">
                                    WORK
                                  </span>
                                ) : (
                                  <ProgramBadge discipline_code={v.discipline_code} program_type={v.program_type} />
                                )}
                                <EventBadge isEvent={String(v.type) === 'event'} />
                                {!isWorkBlock(v) ? <TelehealthBadge v={v} /> : null}
                                <StatusBadge status={v.status} />
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* candidates */}
                      {cands.map((c, idx) => {
                        const box = pctBox(c.from, c.to, dayStartM, spanM);
                        const hard = isHardLimitedForCandidate(c);
                        const barColor = colorForId(isWorkCandidate(c) ? `work-${idx}` : c.student?.id);

                        return (
                          <div
                            key={`c-${idx}`}
                            className="absolute top-5 bottom-2 rounded border border-gray-400 bg-white shadow-sm overflow-hidden z-40"
                            style={{ left: box.left, width: box.width }}
                            title={`Candidate ${c.from}–${c.to} • ${isWorkCandidate(c) ? (c.title || c.work_type || 'Work') : (c.student?.name || '')}`}
                            onPointerDown={(ev) => startDragCandidate(ev, tid, idx, 'move')}
                          >
                            <div className={`h-full ${barColor} opacity-30`} />
                            <div className="absolute inset-0 flex items-center justify-between px-2 text-xs gap-1">
                              <span className="font-mono">{c.from}–{c.to}</span>
                              <span className="hidden sm:inline font-semibold">
                                {isWorkCandidate(c) ? (c.title || `Work: ${c.work_type || 'work'}`) : (c.student?.name || '—')}
                              </span>
                              <div className="flex items-center gap-1">
                                {isWorkCandidate(c) ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200">
                                    WORK
                                  </span>
                                ) : (
                                  <ProgramBadge discipline_code={c.discipline_code} />
                                )}
                                {!isWorkCandidate(c) ? <TelehealthBadge v={c} /> : null}
                                <span className="px-2 py-0.5 rounded text-[10px] bg-gray-50 border border-gray-200 text-gray-700">
                                  candidate
                                </span>
                              </div>
                            </div>

                            {/* resize handles */}
                            <div
                              className="absolute inset-y-0 left-0 w-2 cursor-ew-resize"
                              title={hard ? 'Fixed duration' : 'Resize'}
                              onPointerDown={(ev) => startDragCandidate(ev, tid, idx, 'resize-l')}
                              style={{ opacity: hard ? 0.25 : 1 }}
                            />
                            <div
                              className="absolute inset-y-0 right-0 w-2 cursor-ew-resize"
                              title={hard ? 'Fixed duration' : 'Resize'}
                              onPointerDown={(ev) => startDragCandidate(ev, tid, idx, 'resize-r')}
                              style={{ opacity: hard ? 0.25 : 1 }}
                            />

                            <button
                              type="button"
                              className="absolute -top-2 -right-2 bg-white border border-gray-300 text-red-600 rounded-full w-5 h-5 text-xs leading-5"
                              title="Remove"
                              onClick={(ev) => {
                                ev.preventDefault();
                                ev.stopPropagation();
                                removeCandidate(tid, idx);
                              }}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* LIST under timeline */}
                    <div className="mt-3">
                      <div className="text-xs text-gray-600 mb-1">
                        Items ({listItems.length}) • Drag/Resize applies to <b>candidates</b> only.
                      </div>

                      {!listItems.length ? (
                        <div className="text-xs text-gray-500">No items for this provider on selected day.</div>
                      ) : (
                        <div className="space-y-1">
                          {listItems.map((it) => {
                            const lockedDelete = it.kind === 'saved' ? isLockedSaved(it.raw) : false;
                            const canCancel = it.kind === 'saved' ? isCancelableSaved(it.raw) : false;

                            return (
                              <div
                                key={it.key}
                                className="flex items-center justify-between gap-2 border rounded px-2 py-1 bg-white"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-mono text-xs">{it.from}–{it.to}</span>

                                  <span className="text-xs font-semibold truncate">{it.student_name}</span>

                                  {/* ✅ clinic indicator */}
                                  {(() => {
                                    const raw = it.raw || {};
                                    const isWork = (it.type === 'work') || isWorkBlock(raw) || isWorkCandidate(raw);
                                    const show = isClinicLikeItem(raw, isWork);
                                    if (!show) return null;

                                    const label = resolveClinicLabel(raw.org_location_id);
                                    return (
                                      <span
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-slate-50 text-slate-700 text-[11px] max-w-[260px]"
                                        title={label || 'Clinic'}
                                      >
                                        <Building2 size={14} />
                                        <span className="truncate">{label || 'Clinic'}</span>
                                      </span>
                                    );
                                  })()}

                                  {/* ✅ badges */}
                                  {it.type === 'work' ? (
                                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200">
                                      WORK
                                    </span>
                                  ) : (
                                    <ProgramBadge discipline_code={it.discipline_code} />
                                  )}

                                  {!String(it.type || '').toLowerCase().includes('work') ? <TelehealthBadge v={it} /> : null}

                                  {it.kind === 'saved' ? <StatusBadge status={it.status} /> : (
                                    <span className="px-2 py-0.5 rounded text-[10px] bg-gray-50 border border-gray-200 text-gray-700">
                                      candidate
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center gap-2">
                                  {lockedDelete && (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                                      <FaLock />
                                      Locked
                                    </span>
                                  )}

                                  {it.kind === 'candidate' ? (
                                    <button
                                      type="button"
                                      className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50"
                                      onClick={() => removeCandidate(tid, it.idx)}
                                      title="Remove candidate"
                                    >
                                      <FaTrash />
                                      Remove
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        className={
                                          'inline-flex items-center gap-1 px-2 py-1 text-xs rounded border ' +
                                          (canCancel
                                            ? 'border-purple-200 text-purple-700 hover:bg-purple-50'
                                            : 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50')
                                        }
                                        disabled={!canCancel}
                                        onClick={() => handleCancelSavedVisit(it.raw)}
                                        title={canCancel ? 'Cancel' : 'Not cancelable'}
                                      >
                                        <FaBan />
                                        Cancel
                                      </button>

                                      <button
                                        type="button"
                                        className={
                                          'inline-flex items-center gap-1 px-2 py-1 text-xs rounded border ' +
                                          (lockedDelete
                                            ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50'
                                            : 'border-red-200 text-red-700 hover:bg-red-50')
                                        }
                                        disabled={lockedDelete}
                                        onClick={() => handleDeleteSavedVisit(it.raw)}
                                        title={lockedDelete ? 'Locked' : 'Delete draft'}
                                      >
                                        <FaTrash />
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                  </div>
                );
              })}

              {effectiveTimelineTeacherIds.length === 0 && (
                <div className="text-sm text-gray-600">No teachers selected.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Notes history modal */}
      {showNotesHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">
                Notes History: {notesHistoryStudent}
              </h3>
              <button className="text-gray-500" onClick={() => setShowNotesHistory(false)}>
                ✕
              </button>
            </div>
            {notesHistoryLoading ? (
              <div className="p-4 text-center text-gray-400">Loading...</div>
            ) : notesHistory.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No notes found for this student.</div>
            ) : (
              <div className="space-y-4">
                {notesHistory.map((entry, idx) => (
                  <div key={idx} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex flex-col">
                        <span className="font-semibold text-blue-900">{entry.teacher_name}</span>
                        {entry.discipline_name && (
                          <span className="text-xs text-gray-500">{entry.discipline_name}</span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {entry.scheduled_at ? entry.scheduled_at.slice(0, 16).replace('T', ' ') : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {entry.domains && entry.domains.length > 0 ? (
                        entry.domains.map(dom =>
                          dom.note && dom.note.trim() !== '' ? (
                            <div key={dom.domain_id}>
                              <span className="font-semibold">
                                {dom.name || dom.code || 'Domain'}:
                              </span>{' '}
                              <span>{dom.note}</span>
                            </div>
                          ) : null
                        )
                      ) : (
                        <span className="text-xs text-gray-500 italic">
                          No domain notes for this visit.
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ✅ Cancel modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[3250]">
          <div className="bg-white p-4 rounded max-w-md w-full relative">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={closeCancelModal}
              disabled={cancelSubmitting}
              title={cancelSubmitting ? 'Please wait…' : 'Close'}
            >
              ×
            </button>

            <h3 className="text-lg font-bold mb-2">
              {cancelMode === 'work' ? 'Cancel work block' : 'Cancel visit'}
            </h3>

            <div className="text-sm text-gray-600 mb-3">
              {cancelMode === 'work'
                ? 'Please enter a short reason (required).'
                : 'Please select a reason (required).'}
            </div>

            {cancelMode === 'work' ? (
              <>
                <label className="block text-xs text-gray-600 mb-1">
                  Reason <span className="text-red-600">*</span>
                </label>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm bg-white"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={cancelSubmitting}
                  placeholder="e.g. Schedule changed — work task moved to another time."
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {WORK_CANCEL_QUICK_REASONS.map((txt) => (
                    <button
                      key={txt}
                      type="button"
                      className="text-[11px] px-2 py-1 rounded border bg-slate-50 hover:bg-slate-100"
                      onClick={() => setReason(txt)}
                      disabled={cancelSubmitting}
                    >
                      {txt}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <label className="block text-xs text-gray-600 mb-1">
                  Cancel reason <span className="text-red-600">*</span>
                </label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm bg-white"
                  disabled={cancelSubmitting}
                >
                  <option value="">Select a reason…</option>
                  {REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                className="px-3 py-2 text-sm rounded border hover:bg-gray-50 disabled:opacity-60"
                onClick={closeCancelModal}
                disabled={cancelSubmitting}
              >
                Back
              </button>

              <button
                type="button"
                className="px-3 py-2 text-sm rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                onClick={confirmCancelSavedVisit}
                disabled={!String(reason || '').trim() || cancelSubmitting}
              >
                {cancelSubmitting ? 'Canceling…' : 'Confirm cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Select Student */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[3100]">
          <div className="bg-white p-4 rounded max-w-xl w-full relative max-h-[80vh] overflow-y-auto">
            <button className="absolute top-2 right-2 text-red-500 text-xl" onClick={() => setShowModal(false)}>
              ×
            </button>

            <h3 className="text-lg font-bold mb-2">Select Student</h3>
            <div className="text-sm text-gray-600 mb-2">
              Date: <b>{modalDate}</b> | Start: <b>{modalFrom}</b> | Window to: <b>{modalMaxTo}</b>
            </div>

            {/* Distance controls */}
            <div className="flex flex-wrap items-center gap-3 mb-2 bg-gray-50 rounded p-2">
              <label className="text-sm">
                Distance mode:{' '}
                <select
                  className="border px-2 py-1 text-sm"
                  value={distanceMode}
                  onChange={e => setDistanceMode(e.target.value)}
                >
                  <option value="none">None</option>
                  <option value="haversine">Straight (Haversine)</option>
                  <option value="ors">Route (ORS)</option>
                </select>
              </label>

              {loadingEligible ? (
                <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-white border text-gray-600">
                  {eligibleDefault.length || otherEligible.length ? 'Refreshing…' : 'Loading…'}
                </span>
              ) : null}
            </div>

            <div className="text-xs text-gray-600 mb-2">
              First select a <b>discipline</b> (EI, PI, ABA, OT, PT, ST). Then click <b>In person</b> (at_home) or <b>Telehealth</b>.
              If you want <b>At clinic</b>, pick a clinic location first, then click In person / Telehealth.
              <div className="mt-1 text-[11px] text-gray-500">
                Duration rules: <span className="font-mono">{durationSummaryText}</span>
                {serviceDurationLoading ? <span className="ml-2">(loading…)</span> : null}
              </div>
            </div>

            {/* Work slot */}
            <div className="mb-3 border rounded p-2 bg-slate-50">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold">Work</div>
                  <div className="text-[11px] text-gray-600">
                    Paperwork/admin task for this teacher (30–240 min). Max 6 per day.
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    Today count: <b>{workCountForDay}</b> / 6
                    {modalAnchor?.to ? (
                      <span className="ml-2">Window max: <b>{workMaxMinutes}</b> min</span>
                    ) : null}
                  </div>
                </div>

                <button
                  className={`px-3 py-1.5 rounded text-sm border ${
                    workCountForDay >= 6 || loadingEligible
                      ? 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed'
                      : 'bg-white hover:bg-gray-50'
                  }`}
                  disabled={workCountForDay >= 6 || loadingEligible}
                  onClick={openWorkModal}
                  title={workCountForDay >= 6 ? 'Max 6 work blocks per day' : 'Add work block'}
                >
                  Add Work
                </button>
              </div>
            </div>

            <div className="text-sm font-semibold mb-2">Default students</div>

            {eligibleDefault.length ? (
              eligibleDefault.map(row => {
                const stu = row.student || {};
                const hasPrograms = Array.isArray(stu.program_codes) && stu.program_codes.length > 0;
                const canTelehealth = !!stu.telehealth_available;
                const selectedDisc = selectedDisciplines[stu.id];

                return (
                  <div
                    key={stu.id}
                    className={`mb-2 border p-2 rounded ${row.ok ? 'bg-green-50' : 'bg-yellow-50'}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="font-semibold flex items-center flex-wrap">
                          {(stu.last_name || '') + ', ' + (stu.first_name || '')}
                          <PrevRefPill prev_ref={stu.prev_ref} />
                        </div>
                        <DistanceLine item={stu} distanceMode={distanceMode} />
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        {hasPrograms ? (
                          <DisciplineButtonsRow
                            student={stu}
                            isEligible={row.ok}
                            selectedCode={selectedDisc}
                            onSelectDiscipline={(code) => row.ok && setSelectedDisciplines(prev => ({ ...prev, [stu.id]: code }))}
                          />
                        ) : null}

                        <div className="flex flex-wrap justify-end gap-1">
                          <button
                            className={
                              'px-2 py-0.5 rounded text-xs border ' +
                              (row.ok && selectedDisc
                                ? 'bg-green-600 text-white border-green-700 hover:bg-green-700'
                                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                            }
                            disabled={!row.ok || !selectedDisc}
                            onClick={() => row.ok && selectedDisc && selectStudentWithDiscipline(
                              stu,
                              selectedDisc,
                              'in_person',
                              null,
                              visitLocationMode,
                              (visitLocationMode === 'at_clinic' && selectedClinic) ? Number(selectedClinic.id) : null
                            )}
                          >
                            In person
                          </button>

                          <button
                            className={
                              'px-2 py-0.5 rounded text-xs border ' +
                              (row.ok && canTelehealth && selectedDisc
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-300 hover:bg-indigo-100'
                                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                            }
                            disabled={!row.ok || !canTelehealth || !selectedDisc}
                            onClick={() => row.ok && canTelehealth && selectedDisc && openTelehealthModal(stu, selectedDisc)}
                          >
                            <span className="inline-flex items-center gap-1">
                              <span className="text-xs">📹</span>
                              <span>{stu.telehealth_label || 'Telehealth'}</span>
                            </span>
                          </button>

                          {/* ✅ At clinic replaces Event */}
                          <button
                            className={
                              'px-2 py-0.5 rounded text-xs border ' +
                              (row.ok
                                ? (visitLocationMode === 'at_clinic'
                                  ? 'bg-yellow-50 text-orange-700 border-orange-300 hover:bg-yellow-100'
                                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100')
                                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                            }
                            disabled={!row.ok}
                            title="At clinic (pick location)"
                            onClick={() => row.ok && openClinicPicker()}
                          >
                            At clinic
                          </button>
                        </div>

                        {visitLocationMode === 'at_clinic' && selectedClinic ? (
                          <div className="text-[11px] text-gray-500 text-right">
                            Clinic: <b>{selectedClinic.name || `#${selectedClinic.id}`}</b>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {!row.ok && row.reason && (
                      <div className="text-xs text-gray-700 mt-1">{row.reason}</div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-gray-600">No default students.</div>
            )}

            <hr className="my-3" />
            <div className="text-sm font-semibold mb-2">Other students (available now)</div>

            {otherEligible.length ? (
              otherEligible.map(stu => {
                const hasPrograms = Array.isArray(stu.program_codes) && stu.program_codes.length > 0;
                const canTelehealth = !!stu.telehealth_available;
                const selectedDisc = selectedDisciplines[stu.id];

                return (
                  <div key={stu.id} className="mb-2 border p-2 rounded bg-blue-50">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="font-semibold flex items-center flex-wrap">
                          {(stu.last_name || '') + ', ' + (stu.first_name || '')}
                          <PrevRefPill prev_ref={stu.prev_ref} />
                        </div>
                        <DistanceLine item={stu} distanceMode={distanceMode} />
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        {hasPrograms ? (
                          <DisciplineButtonsRow
                            student={stu}
                            isEligible={true}
                            selectedCode={selectedDisc}
                            onSelectDiscipline={(code) => setSelectedDisciplines(prev => ({ ...prev, [stu.id]: code }))}
                          />
                        ) : null}

                        <div className="flex flex-wrap justify-end gap-1">
                          <button
                            className={
                              'px-2 py-0.5 rounded text-xs border ' +
                              (selectedDisc
                                ? 'bg-blue-600 text-white border-blue-700 hover:bg-blue-700'
                                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                            }
                            disabled={!selectedDisc}
                            onClick={() => selectedDisc && selectStudentWithDiscipline(
                              stu,
                              selectedDisc,
                              'in_person',
                              null,
                              visitLocationMode,
                              (visitLocationMode === 'at_clinic' && selectedClinic) ? Number(selectedClinic.id) : null
                            )}
                          >
                            In person
                          </button>

                          <button
                            className={
                              'px-2 py-0.5 rounded text-xs border ' +
                              (canTelehealth && selectedDisc
                                ? 'bg-indigo-50 text-indigo-700 border-indigo-300 hover:bg-indigo-100'
                                : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed')
                            }
                            disabled={!canTelehealth || !selectedDisc}
                            onClick={() => canTelehealth && selectedDisc && openTelehealthModal(stu, selectedDisc)}
                          >
                            <span className="inline-flex items-center gap-1">
                              <span className="text-xs">📹</span>
                              <span>{stu.telehealth_label || 'Telehealth'}</span>
                            </span>
                          </button>

                          <button
                            className={
                              'px-2 py-0.5 rounded text-xs border ' +
                              (visitLocationMode === 'at_clinic'
                                ? 'bg-yellow-50 text-orange-700 border-orange-300 hover:bg-yellow-100'
                                : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100')
                            }
                            onClick={() => openClinicPicker()}
                            title="At clinic (pick location)"
                            type="button"
                          >
                            At clinic
                          </button>
                        </div>

                        {visitLocationMode === 'at_clinic' && selectedClinic ? (
                          <div className="text-[11px] text-gray-500 text-right">
                            Clinic: <b>{selectedClinic.name || `#${selectedClinic.id}`}</b>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-gray-600">No other eligible students.</div>
            )}
          </div>
        </div>
      )}

      {/* ✅ Clinic picker modal */}
      {clinicModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[3175]">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5 relative">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={() => setClinicModalOpen(false)}
              title="Close"
            >
              ×
            </button>

            <h3 className="text-lg font-bold mb-2">Select clinic location</h3>

            <div className="text-sm text-gray-600 mb-3">
              Choose an organization location (required for at_clinic).
            </div>

            <label className="block text-sm font-medium mb-1">Clinic location</label>
            <select
              className="w-full border rounded px-2 py-2 text-sm bg-white"
              value={selectedClinic ? String(selectedClinic.id) : ''}
              onChange={(e) => {
                const id = Number(e.target.value || 0);
                const found = (orgLocations || []).find(x => Number(x.id) === id);
                if (found) {
                  setSelectedClinic({ id: found.id, name: found.name, geocoded_label: found.geocoded_label });
                } else {
                  setSelectedClinic(null);
                }
              }}
              disabled={orgLocationsLoading}
            >
              <option value="">{orgLocationsLoading ? 'Loading…' : 'Select clinic…'}</option>
              {(orgLocations || []).map((ol) => (
                <option key={ol.id} value={ol.id}>
                  {(ol.name || 'Location') + (ol.geocoded_label ? ` — ${ol.geocoded_label}` : '')}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100"
                onClick={() => setClinicModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-2 text-sm rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                onClick={() => {
                  if (!selectedClinic) return;
                  setVisitLocationMode('at_clinic');
                  setClinicModalOpen(false);
                }}
                disabled={!selectedClinic}
              >
                Use clinic
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Work modal */}
      {workModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[3300]">
          <div className="bg-white p-4 rounded max-w-md w-full relative">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={closeWorkModal}
              disabled={workSubmitting}
            >
              ×
            </button>

            <h3 className="text-lg font-bold mb-2">Add Work Block</h3>

            <div className="text-sm text-gray-700 mb-3">
              Date: <b>{modalDate}</b> | Start: <b>{modalFrom}</b>
              {modalAnchor?.to ? (
                <span> | Window to: <b>{modalAnchor.to}</b></span>
              ) : null}
            </div>

            <div className="mb-3">
              <label className="text-sm font-semibold">Location</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm mt-1"
                value={workForm.location_value || ''}
                onChange={(e) => setWorkForm(prev => ({ ...prev, location_value: e.target.value }))}
                disabled={orgLocationsLoading || workSubmitting}
              >
                <option value="">{orgLocationsLoading ? 'Loading…' : 'Select location'}</option>

                {orgLocations.map(loc => (
                  <option key={loc.id} value={`org:${loc.id}`}>
                    {`Org: ${loc.name || 'Location'}${loc.city ? ` (${loc.city})` : ''}`}
                  </option>
                ))}

                <option value="teacher_home">Teacher home</option>
                <option value="remote">Remote</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="text-sm font-semibold">Work type</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm mt-1"
                value={workForm.work_type || 'admin'}
                onChange={(e) => setWorkForm(prev => ({ ...prev, work_type: e.target.value }))}
                disabled={workSubmitting}
              >
                <option value="admin">admin</option>
                <option value="documentation">documentation</option>
                <option value="training">training</option>
                <option value="research">research</option>
                <option value="meeting">meeting</option>
                <option value="other">other</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="text-sm font-semibold">Minutes (30–{workMaxMinutes})</label>
              <input
                type="number"
                min={30}
                max={workMaxMinutes}
                value={workForm.planned_minutes ?? ''}
                onChange={(e) => {
                  setWorkForm(prev => ({ ...prev, planned_minutes: e.target.value }));
                }}
				onBlur={() => {
					const maxM = computeWorkMaxM(modalFrom, modalAnchor?.to);
					const fixed = normalizePlannedMinutes(workForm.planned_minutes, maxM);
					setWorkForm(prev => ({ ...prev, planned_minutes: String(fixed) }));
				}}
                className="w-full border rounded px-3 py-2 text-sm mt-1"
                disabled={workSubmitting}
              />
              <div className="text-[11px] text-gray-500 mt-1">
                Saved only on “Save (Selected Day)”.
              </div>
            </div>

            <div className="mb-3">
              <label className="text-sm font-semibold">Title (optional)</label>
              <input
                type="text"
                value={workForm.title || ''}
                onChange={(e) => setWorkForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm mt-1"
                maxLength={160}
                disabled={workSubmitting}
                placeholder="e.g., Paperwork"
              />
            </div>

            <div className="mb-4">
              <label className="text-sm font-semibold">Notes (optional)</label>
              <textarea
                value={workForm.notes || ''}
                onChange={(e) => setWorkForm(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm mt-1"
                rows={3}
                disabled={workSubmitting}
                placeholder="Optional notes…"
              />
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                className="px-3 py-2 rounded border bg-white hover:bg-gray-50 text-sm"
                onClick={closeWorkModal}
                disabled={workSubmitting}
              >
                Cancel
              </button>
              <button
                className={`px-3 py-2 rounded text-sm text-white ${
                  workSubmitting ? 'bg-slate-400' : 'bg-sky-700 hover:bg-sky-800'
                }`}
                onClick={submitWork}
                disabled={workSubmitting}
              >
                {workSubmitting ? 'Saving…' : 'Add Work'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Telehealth modal */}
      {showTeleModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[3200]">
          <div className="bg-white p-4 rounded max-w-md w-full relative">
            <button
              className="absolute top-2 right-2 text-red-500 text-xl"
              onClick={cancelTelehealthModal}
            >
              ×
            </button>

            <h3 className="text-lg font-bold mb-2">Telehealth link</h3>
            <div className="text-sm text-gray-600 mb-2">
              Student: <b>{teleStudent ? `${teleStudent.last_name || ''}, ${teleStudent.first_name || ''}` : '—'}</b>
            </div>

            <input
              value={teleLink}
              onChange={(e) => setTeleLink(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="https://..."
            />

            {teleError ? <div className="text-sm text-red-600 mt-2">{teleError}</div> : null}

            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                className="px-3 py-2 text-sm rounded border hover:bg-gray-50"
                onClick={cancelTelehealthModal}
              >
                Back
              </button>
              <button
                type="button"
                className="px-3 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={handleTelehealthSave}
                disabled={!String(teleLink || '').trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}















